import * as THREE from 'three';
import { SceneManager } from './scenes/SceneManager';
import { MaterialManager } from './scenes/MaterialManager';
import { UIManager } from './components/UIManager';
import { EditorUI } from './components/EditorUI';
import { MaterialSelector } from './components/MaterialSelector';
import { FabpFormat, formatFileSize } from './utils';
import { canUseDemoQuota, consumeDemoQuota, ensureDemoBadge, getDemoLimitMessage, getDemoRestrictionMessage, isDemoMode, type DemoQuotaKind } from './utils/demoMode';
import { showSupportChoice } from './utils/support';
import { authService, loginModal } from './auth';
import { i18n, t } from './i18n/translations';
import { updateSeo } from './utils/seo';
import JSZip from 'jszip';

const DEMO_MODE = isDemoMode();

class App {
  private sceneManager: SceneManager;
  private materialManager: MaterialManager;
  private uiManager: UIManager;
  private editorUI: EditorUI;
  private materialSelector: MaterialSelector;
  private animationId: number = 0;
  private fpsCounter: HTMLElement | null = null;
  private canvasInfo: HTMLElement | null = null;
  private objectsInfo: HTMLElement | null = null;
  private frameCount: number = 0;
  private lastFpsUpdate: number = 0;
  private fps: number = 0;
  private projectStartTime: number = Date.now();
  private projectName: string = 'Без названия';
  private projectFileName: string = ''; // Имя файла проекта (.fabp)
  private totalWorkTime: number = 0; // Накопленное время работы в миллисекундах
  private workTimeSaveInterval: number = 0;
  private lastSavedFileHandle: FileSystemFileHandle | null = null; // Для быстрого сохранения Cmd+S
  private isNewProject: boolean = true; // Флаг "новый проект" (не сохранялся ни разу)
  private overlayCancelAction: (() => void) | null = null;
  private overlayPrimaryAction: (() => void) | null = null;
  
  // FPS -> performance mode hint
  private fpsPerfPanel: HTMLElement | null = null;
  private perfModeToggleFps: HTMLInputElement | null = null;
  private perfModeToggleSettings: HTMLInputElement | null = null;
  private fpsPerfPanelMessage: HTMLElement | null = null;
  private fpsPerfPanelClose: HTMLButtonElement | null = null;
  private fpsPerfPanelDismissed: boolean = false;
  private fpsPerfAutoHideTimeout: number = 0;
  private optimizationOnBadge: HTMLElement | null = null;
  private lowFpsMs: number = 0;
  private highFpsMs: number = 0;
  private readonly FPS_THRESHOLD: number = 30;
  private readonly FPS_STABLE_MS: number = 3000;

  private showDemoRestriction(action: 'save-project' | 'load-project' | 'new-project' | 'load-model'): void {
    this.sceneManager.showToolbarMessage(getDemoRestrictionMessage(action), 2800);
  }

  private canUseDemoExport(kind: DemoQuotaKind): boolean {
    if (!DEMO_MODE) return true;
    if (canUseDemoQuota(kind)) return true;
    this.sceneManager.showToolbarMessage(getDemoLimitMessage(kind), 3000);
    return false;
  }

  constructor() {
    // Показываем начальную загрузку
    this.uiManager = new UIManager();
    this.uiManager.showLoadingOverlay('Инициализация приложения...', 0, true);
    
    this.materialManager = new MaterialManager(this.uiManager.getUIControls());
    
    try {
      this.uiManager.updateLoadingOverlay('Инициализация 3D сцены...', 20);
      this.sceneManager = new SceneManager(
        document.getElementById('canvas-container')!,
        this.materialManager
      );
    } catch (error) {
      // Показываем user-friendly сообщение об ошибке WebGL
      this.showWebGLError();
      throw error; // Прокидываем ошибку дальше чтобы остановить инициализацию
    }
    
    this.uiManager.updateLoadingOverlay('Настройка материалов...', 40);
    this.materialManager.sceneManager = this.sceneManager;

    // Apply saved rotation control mode early (scene vs model)
    try {
      const saved = (localStorage.getItem('fabrica.rotationControlMode') as 'scene' | 'model' | null) || 'model';
      this.sceneManager.setRotationControlMode(saved);
    } catch {
      // ignore
    }
    
    this.uiManager.updateLoadingOverlay('Подготовка редактора...', 60);
    this.editorUI = new EditorUI();
    this.editorUI.setSceneManager(this.sceneManager);
    this.materialSelector = new MaterialSelector(this.sceneManager);
    
    this.uiManager.updateLoadingOverlay('Загрузка интерфейса...', 80);
    this.setupEventListeners();
    this.initPerformanceMonitor();
    this.initMaterialSelectorPanel();
    
    this.uiManager.updateLoadingOverlay('Запуск рендера...', 90);
    this.startAnimation();
    this.loadPresets();
    this.updateDocumentTitle(); // Устанавливаем начальное название вкладки
    if (DEMO_MODE) ensureDemoBadge();
    
    // Даём время на первый кадр рендеринга
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.uiManager.hideLoadingOverlay();
      });
    });
  }
  
  private initPerformanceMonitor(): void {
    this.fpsCounter = document.getElementById('fps-counter');
    this.canvasInfo = document.getElementById('canvas-info');
    this.objectsInfo = document.getElementById('objects-info');
    
    // Separate auto low-FPS panel near File button
    this.fpsPerfPanel = document.getElementById('fps-performance-panel');
    this.perfModeToggleFps = document.getElementById('performance-mode-toggle-fps') as HTMLInputElement | null;
    this.perfModeToggleSettings = document.getElementById('performance-mode-toggle-settings') as HTMLInputElement | null;
    this.fpsPerfPanelMessage = document.getElementById('fps-performance-message');
    this.fpsPerfPanelClose = document.getElementById('fps-performance-close') as HTMLButtonElement | null;
    this.fpsPerfPanelDismissed = this.sceneManager.isFpsPanelDismissed();

    // Apply initial UI state
    if (this.fpsPerfPanelDismissed) {
      this.fpsPerfPanel?.classList.add('hidden');
    }

    // Sync toggles initial state
    const syncToggles = (checked: boolean) => {
      if (this.perfModeToggleFps) this.perfModeToggleFps.checked = checked;
      if (this.perfModeToggleSettings) this.perfModeToggleSettings.checked = checked;
    };
    syncToggles(this.sceneManager.isPerformanceModeEnabled());

    // Badge "Оптимизация ВКЛ" next to logo — show when optimization on, click opens settings
    this.optimizationOnBadge = document.getElementById('optimization-on-badge');
    const updateOptimizationBadgeVisibility = () => {
      if (!this.optimizationOnBadge) return;
      const enabled = this.sceneManager.isPerformanceModeEnabled();
      if (enabled) this.optimizationOnBadge.classList.remove('hidden');
      else this.optimizationOnBadge.classList.add('hidden');
    };
    updateOptimizationBadgeVisibility();
    this.optimizationOnBadge?.addEventListener('click', (e) => {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('showSettingsPanel', { detail: { tab: 'optimization' } }));
    });

    // Close button: dismiss auto low-FPS panel for this project
    this.fpsPerfPanelClose?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.fpsPerfPanel?.classList.add('hidden');
      this.lowFpsMs = 0;
      this.highFpsMs = 0;
      if (this.fpsPerfAutoHideTimeout) {
        clearTimeout(this.fpsPerfAutoHideTimeout);
        this.fpsPerfAutoHideTimeout = 0;
      }
      this.fpsPerfPanelDismissed = true;
      this.sceneManager.setFpsPanelDismissed(true);
    });

    // Update app-level UI state when project is loaded
    window.addEventListener('projectUiLoaded', ((evt: Event) => {
      const ui = (evt as CustomEvent).detail as any;
      const dismissed = Boolean(ui?.fpsPanelDismissed);
      this.fpsPerfPanelDismissed = dismissed;
      if (dismissed) this.fpsPerfPanel?.classList.add('hidden');
      else this.fpsPerfPanelMessage && (this.fpsPerfPanelMessage.textContent = t('perf.low.message'));

      const rotMode = ui?.rotationControlMode;
      if (rotMode === 'scene' || rotMode === 'model') {
        try { localStorage.setItem('fabrica.rotationControlMode', rotMode); } catch {}
        // Sync UI buttons if present
        const sceneBtn = document.getElementById('rotation-mode-scene');
        const modelBtn = document.getElementById('rotation-mode-model');
        sceneBtn?.classList.toggle('active', rotMode === 'scene');
        modelBtn?.classList.toggle('active', rotMode === 'model');
      }
    }) as EventListener);

    const onPerfModeChanged = (enabled: boolean) => {
      this.sceneManager.setPerformanceModeEnabled(enabled);
      syncToggles(enabled);
      updateOptimizationBadgeVisibility();

      // If panel is visible and user enabled optimization, auto-hide panel only when it was auto-shown (low FPS),
      // not via manual UI (this panel itself is auto-only).
      if (
        !this.fpsPerfPanelDismissed &&
        enabled &&
        this.fpsPerfPanel &&
        !this.fpsPerfPanel.classList.contains('hidden')
      ) {
        if (this.fpsPerfAutoHideTimeout) clearTimeout(this.fpsPerfAutoHideTimeout);
        this.fpsPerfAutoHideTimeout = window.setTimeout(() => {
          this.fpsPerfPanel?.classList.add('hidden');
          this.fpsPerfAutoHideTimeout = 0;
        }, 3000);
      }
    };

    const attachToggle = (toggle: HTMLInputElement | null) => {
      toggle?.addEventListener('change', () => {
        onPerfModeChanged(Boolean(toggle.checked));
      });
    };
    attachToggle(this.perfModeToggleFps);
    attachToggle(this.perfModeToggleSettings);
    
    this.lastFpsUpdate = performance.now();
    this.initInfoModal();
    this.initDebugPanel();
  }
  
  private showWebGLError(): void {
    // Скрываем загрузочный экран если он есть
    const loadingOverlay = document.getElementById('loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
    
    // Создаем минималистичный overlay в стиле loading-overlay
    const errorOverlay = document.createElement('div');
    errorOverlay.id = 'webgl-error-overlay';
    errorOverlay.className = 'loading-overlay';
    errorOverlay.classList.remove('hidden');
    
    errorOverlay.innerHTML = `
      <div class="loading-overlay-content">
        <div class="loading-overlay-text">${t('error.webgl.title')}</div>
        <div class="loading-overlay-warning" style="display: block;">${t('error.webgl.description')}</div>
        <div class="loading-overlay-actions">
          <button class="loading-overlay-cancel loading-overlay-action" onclick="location.reload()">${t('error.webgl.retry')}</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(errorOverlay);
  }

  private updateFpsPerformancePanel(elapsedMs: number): void {
    if (!this.fpsPerfPanel) return;
    if (this.fpsPerfPanelDismissed) return;
    const isPerfEnabled = this.sceneManager.isPerformanceModeEnabled();

    // Накопление "стабильности" — работаем по секундным обновлениям FPS
    if (this.fps < this.FPS_THRESHOLD) {
      this.lowFpsMs += elapsedMs;
      this.highFpsMs = 0;
    } else {
      this.highFpsMs += elapsedMs;
      this.lowFpsMs = 0;
    }

    const stableLow = this.lowFpsMs >= this.FPS_STABLE_MS;
    const stableHigh = this.highFpsMs >= this.FPS_STABLE_MS;

    // Показываем панель при стабильном низком FPS (только если оптимизация НЕ включена)
    if (stableLow && !isPerfEnabled) {
      if (this.fpsPerfPanelMessage) {
        this.fpsPerfPanelMessage.textContent = t('perf.low.message');
      }
      this.fpsPerfPanel.classList.remove('hidden');
      return;
    }

    // Если стабильно стало лучше — прячем панель (и тем самым "отключаем действия")
    if (stableHigh) {
      this.fpsPerfPanel.classList.add('hidden');
      this.fpsPerfPanelMessage?.classList.add('hidden');
    }
  }
  
  private infoPanelUpdateInterval: number = 0;
  
  private initInfoModal(): void {
    const infoPanel = document.getElementById('info-panel');
    const closeBtn = document.getElementById('info-panel-close');
    const header = document.getElementById('info-panel-header');
    
    // Закрытие панели
    closeBtn?.addEventListener('click', () => {
      infoPanel?.classList.add('hidden');
      if (this.infoPanelUpdateInterval) {
        clearInterval(this.infoPanelUpdateInterval);
        this.infoPanelUpdateInterval = 0;
      }
    });
    
    // Открытие панели
    window.addEventListener('showInfoModal', () => {
      this.updateInfoModal();
      infoPanel?.classList.remove('hidden');
      // Периодическое обновление FPS пока панель открыта
      if (!this.infoPanelUpdateInterval) {
        this.infoPanelUpdateInterval = window.setInterval(() => {
          if (!infoPanel?.classList.contains('hidden')) {
            this.updateInfoModal();
          }
        }, 500);
      }
    });
    
    // Hide info modal (used when settings panel closes)
    window.addEventListener('hideInfoModal', () => {
      infoPanel?.classList.add('hidden');
      if (this.infoPanelUpdateInterval) {
        clearInterval(this.infoPanelUpdateInterval);
        this.infoPanelUpdateInterval = 0;
      }
    });
    
    // Drag functionality
    if (header && infoPanel) {
      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let startLeft = 0;
      let startTop = 0;
      
      header.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.clientX;
        startY = e.clientY;
        const rect = infoPanel.getBoundingClientRect();
        startLeft = rect.left;
        startTop = rect.top;
        e.preventDefault();
      });
      
      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        infoPanel.style.left = `${startLeft + dx}px`;
        infoPanel.style.top = `${startTop + dy}px`;
      });
      
      document.addEventListener('mouseup', () => {
        isDragging = false;
      });
    }
  }
  
  private initMaterialSelectorPanel(): void {
    const materialPanel = document.getElementById('material-selector-panel');
    const header = document.getElementById('material-selector-header');
    
    // Drag functionality
    if (header && materialPanel) {
      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let startLeft = 0;
      let startTop = 0;
      
      header.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.clientX;
        startY = e.clientY;
        const rect = materialPanel.getBoundingClientRect();
        startLeft = rect.left;
        startTop = rect.top;
        // Переключаемся на left/top позиционирование для drag
        materialPanel.style.right = 'auto';
        materialPanel.style.top = `${startTop}px`;
        materialPanel.style.left = `${startLeft}px`;
        materialPanel.style.transform = 'none';
        e.preventDefault();
      });
      
      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        materialPanel.style.left = `${startLeft + dx}px`;
        materialPanel.style.top = `${startTop + dy}px`;
      });
      
      document.addEventListener('mouseup', () => {
        isDragging = false;
      });
    }
  }
  
  private initDebugPanel(): void {
    const debugPanel = document.getElementById('debug-panel');
    const closeBtn = document.getElementById('close-debug-panel');
    
    // Закрытие панели
    closeBtn?.addEventListener('click', () => {
      debugPanel?.classList.add('hidden');
    });
    
    // Открытие панели
    window.addEventListener('showDebugPanel', () => {
      debugPanel?.classList.remove('hidden');
    });
    
    // Инициализация аккордеона секций
    const sectionHeaders = debugPanel?.querySelectorAll('.effects-section-header');
    sectionHeaders?.forEach(header => {
      header.addEventListener('click', () => {
        const sectionName = header.getAttribute('data-section');
        const content = document.getElementById(`section-${sectionName}`);
        
        if (content) {
          const isOpen = !content.classList.contains('hidden');
          
          if (isOpen) {
            content.classList.add('hidden');
            header.classList.remove('open');
          } else {
            content.classList.remove('hidden');
            header.classList.add('open');
          }
        }
      });
    });
    
    // Инициализация перетаскивания панели
    const dragHandle = debugPanel?.querySelector('.effects-panel-drag-handle');
    if (debugPanel && dragHandle) {
      let isDragging = false;
      let offsetX = 0;
      let offsetY = 0;
      
      dragHandle.addEventListener('mousedown', (e: Event) => {
        const mouseEvent = e as MouseEvent;
        // Игнорируем клик по кнопке закрытия
        if ((mouseEvent.target as HTMLElement).tagName === 'BUTTON') return;
        
        isDragging = true;
        const rect = debugPanel.getBoundingClientRect();
        offsetX = mouseEvent.clientX - rect.left;
        offsetY = mouseEvent.clientY - rect.top;
        
        // Сбрасываем transform при начале перетаскивания
        debugPanel.style.transform = 'none';
        debugPanel.style.left = `${rect.left}px`;
        debugPanel.style.top = `${rect.top}px`;
        debugPanel.style.right = 'auto';
        
        document.body.style.userSelect = 'none';
      });
      
      document.addEventListener('mousemove', (e: MouseEvent) => {
        if (!isDragging) return;
        
        const newX = e.clientX - offsetX;
        const newY = e.clientY - offsetY;
        
        // Ограничиваем перемещение в пределах окна
        const maxX = window.innerWidth - debugPanel.offsetWidth;
        const maxY = window.innerHeight - debugPanel.offsetHeight;
        
        debugPanel.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
        debugPanel.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
      });
      
      document.addEventListener('mouseup', () => {
        if (isDragging) {
          isDragging = false;
          document.body.style.userSelect = '';
        }
      });
    }
    
    // Ползунки и контролы
    const pixelRatioSlider = document.getElementById('debug-pixel-ratio') as HTMLInputElement;
    const pixelRatioVal = document.getElementById('debug-pixel-ratio-val');
    if (pixelRatioSlider && pixelRatioVal) {
      pixelRatioSlider.addEventListener('input', () => {
        const val = parseFloat(pixelRatioSlider.value);
        pixelRatioVal.textContent = val.toFixed(1);
        this.sceneManager.setPixelRatio(val);
      });
      pixelRatioVal.textContent = pixelRatioSlider.value;
    }
    
    const toneExposureSlider = document.getElementById('debug-tone-exposure') as HTMLInputElement;
    const toneExposureVal = document.getElementById('debug-tone-exposure-val');
    if (toneExposureSlider && toneExposureVal) {
      toneExposureSlider.addEventListener('input', () => {
        const val = parseFloat(toneExposureSlider.value);
        toneExposureVal.textContent = val.toFixed(1);
        this.sceneManager.setToneMappingExposure(val);
      });
      toneExposureVal.textContent = toneExposureSlider.value;
    }

    // Postprocessing: Bloom
    const bloomEnabledCheck = document.getElementById('debug-bloom-enabled') as HTMLInputElement;
    if (bloomEnabledCheck) {
      bloomEnabledCheck.addEventListener('change', () => {
        this.sceneManager.setBloomEnabled(bloomEnabledCheck.checked);
      });
    }
    const bloomStrengthSlider = document.getElementById('debug-bloom-strength') as HTMLInputElement;
    const bloomStrengthVal = document.getElementById('debug-bloom-strength-val');
    if (bloomStrengthSlider && bloomStrengthVal) {
      bloomStrengthSlider.addEventListener('input', () => {
        const val = parseFloat(bloomStrengthSlider.value);
        bloomStrengthVal.textContent = val.toFixed(2);
        this.sceneManager.setBloomStrength(val);
      });
      bloomStrengthVal.textContent = parseFloat(bloomStrengthSlider.value).toFixed(2);
    }
    const bloomRadiusSlider = document.getElementById('debug-bloom-radius') as HTMLInputElement;
    const bloomRadiusVal = document.getElementById('debug-bloom-radius-val');
    if (bloomRadiusSlider && bloomRadiusVal) {
      bloomRadiusSlider.addEventListener('input', () => {
        const val = parseFloat(bloomRadiusSlider.value);
        bloomRadiusVal.textContent = val.toFixed(2);
        this.sceneManager.setBloomRadius(val);
      });
      bloomRadiusVal.textContent = parseFloat(bloomRadiusSlider.value).toFixed(2);
    }
    const bloomThresholdSlider = document.getElementById('debug-bloom-threshold') as HTMLInputElement;
    const bloomThresholdVal = document.getElementById('debug-bloom-threshold-val');
    if (bloomThresholdSlider && bloomThresholdVal) {
      bloomThresholdSlider.addEventListener('input', () => {
        const val = parseFloat(bloomThresholdSlider.value);
        bloomThresholdVal.textContent = val.toFixed(2);
        this.sceneManager.setBloomThreshold(val);
      });
      bloomThresholdVal.textContent = parseFloat(bloomThresholdSlider.value).toFixed(2);
    }

    // Postprocessing: DOF
    const dofEnabledCheck = document.getElementById('debug-dof-enabled') as HTMLInputElement;
    if (dofEnabledCheck) {
      dofEnabledCheck.addEventListener('change', () => {
        this.sceneManager.setDOFEnabled(dofEnabledCheck.checked);
      });
    }
    const dofFocusSlider = document.getElementById('debug-dof-focus') as HTMLInputElement;
    const dofFocusVal = document.getElementById('debug-dof-focus-val');
    if (dofFocusSlider && dofFocusVal) {
      dofFocusSlider.addEventListener('input', () => {
        const val = parseFloat(dofFocusSlider.value);
        dofFocusVal.textContent = val.toFixed(2);
        this.sceneManager.setDOFFocus(val);
      });
      dofFocusVal.textContent = parseFloat(dofFocusSlider.value).toFixed(2);
    }
    const dofApertureSlider = document.getElementById('debug-dof-aperture') as HTMLInputElement;
    const dofApertureVal = document.getElementById('debug-dof-aperture-val');
    if (dofApertureSlider && dofApertureVal) {
      dofApertureSlider.addEventListener('input', () => {
        const val = parseFloat(dofApertureSlider.value);
        dofApertureVal.textContent = val.toFixed(4);
        this.sceneManager.setDOFAperture(val);
      });
      dofApertureVal.textContent = parseFloat(dofApertureSlider.value).toFixed(4);
    }
    const dofMaxBlurSlider = document.getElementById('debug-dof-maxblur') as HTMLInputElement;
    const dofMaxBlurVal = document.getElementById('debug-dof-maxblur-val');
    if (dofMaxBlurSlider && dofMaxBlurVal) {
      dofMaxBlurSlider.addEventListener('input', () => {
        const val = parseFloat(dofMaxBlurSlider.value);
        dofMaxBlurVal.textContent = val.toFixed(4);
        this.sceneManager.setDOFMaxBlur(val);
      });
      dofMaxBlurVal.textContent = parseFloat(dofMaxBlurSlider.value).toFixed(4);
    }

    // Postprocessing: Film / Grain
    const filmEnabledCheck = document.getElementById('debug-film-enabled') as HTMLInputElement;
    if (filmEnabledCheck) {
      filmEnabledCheck.addEventListener('change', () => {
        this.sceneManager.setFilmEnabled(filmEnabledCheck.checked);
      });
    }
    const filmNoiseSlider = document.getElementById('debug-film-noise') as HTMLInputElement;
    const filmNoiseVal = document.getElementById('debug-film-noise-val');
    if (filmNoiseSlider && filmNoiseVal) {
      filmNoiseSlider.addEventListener('input', () => {
        const val = parseFloat(filmNoiseSlider.value);
        filmNoiseVal.textContent = val.toFixed(2);
        this.sceneManager.setFilmNoiseIntensity(val);
      });
      filmNoiseVal.textContent = parseFloat(filmNoiseSlider.value).toFixed(2);
    }
    const filmScanlinesSlider = document.getElementById('debug-film-scanlines') as HTMLInputElement;
    const filmScanlinesVal = document.getElementById('debug-film-scanlines-val');
    if (filmScanlinesSlider && filmScanlinesVal) {
      filmScanlinesSlider.addEventListener('input', () => {
        const val = parseFloat(filmScanlinesSlider.value);
        filmScanlinesVal.textContent = val.toFixed(2);
        this.sceneManager.setFilmScanlinesIntensity(val);
      });
      filmScanlinesVal.textContent = parseFloat(filmScanlinesSlider.value).toFixed(2);
    }
    const filmScanlinesCountSlider = document.getElementById('debug-film-scanlines-count') as HTMLInputElement;
    const filmScanlinesCountVal = document.getElementById('debug-film-scanlines-count-val');
    if (filmScanlinesCountSlider && filmScanlinesCountVal) {
      filmScanlinesCountSlider.addEventListener('input', () => {
        const val = parseFloat(filmScanlinesCountSlider.value);
        filmScanlinesCountVal.textContent = String(Math.round(val));
        this.sceneManager.setFilmScanlinesCount(Math.round(val));
      });
      filmScanlinesCountVal.textContent = filmScanlinesCountSlider.value;
    }

    // Postprocessing: Color correction
    const ccEnabledCheck = document.getElementById('debug-cc-enabled') as HTMLInputElement;
    if (ccEnabledCheck) {
      ccEnabledCheck.addEventListener('change', () => {
        this.sceneManager.setColorCorrectionEnabled(ccEnabledCheck.checked);
      });
    }
    const ccBrightnessSlider = document.getElementById('debug-cc-brightness') as HTMLInputElement;
    const ccBrightnessVal = document.getElementById('debug-cc-brightness-val');
    if (ccBrightnessSlider && ccBrightnessVal) {
      ccBrightnessSlider.addEventListener('input', () => {
        const val = parseFloat(ccBrightnessSlider.value);
        ccBrightnessVal.textContent = val.toFixed(4);
        this.sceneManager.setColorCorrectionBrightness(val);
      });
      ccBrightnessVal.textContent = parseFloat(ccBrightnessSlider.value).toFixed(4);
    }
    const ccContrastSlider = document.getElementById('debug-cc-contrast') as HTMLInputElement;
    const ccContrastVal = document.getElementById('debug-cc-contrast-val');
    if (ccContrastSlider && ccContrastVal) {
      ccContrastSlider.addEventListener('input', () => {
        const val = parseFloat(ccContrastSlider.value);
        ccContrastVal.textContent = val.toFixed(4);
        this.sceneManager.setColorCorrectionContrast(val);
      });
      ccContrastVal.textContent = parseFloat(ccContrastSlider.value).toFixed(4);
    }
    const ccSaturationSlider = document.getElementById('debug-cc-saturation') as HTMLInputElement;
    const ccSaturationVal = document.getElementById('debug-cc-saturation-val');
    if (ccSaturationSlider && ccSaturationVal) {
      ccSaturationSlider.addEventListener('input', () => {
        const val = parseFloat(ccSaturationSlider.value);
        ccSaturationVal.textContent = val.toFixed(4);
        this.sceneManager.setColorCorrectionSaturation(val);
      });
      ccSaturationVal.textContent = parseFloat(ccSaturationSlider.value).toFixed(4);
    }

    // Postprocessing: SSAO
    const ssaoEnabledCheck = document.getElementById('debug-ssao-enabled') as HTMLInputElement;
    if (ssaoEnabledCheck) {
      ssaoEnabledCheck.addEventListener('change', () => {
        this.sceneManager.setSSAOEnabled(ssaoEnabledCheck.checked);
      });
    }
    const ssaoRadiusSlider = document.getElementById('debug-ssao-radius') as HTMLInputElement;
    const ssaoRadiusVal = document.getElementById('debug-ssao-radius-val');
    if (ssaoRadiusSlider && ssaoRadiusVal) {
      ssaoRadiusSlider.addEventListener('input', () => {
        const val = parseFloat(ssaoRadiusSlider.value);
        ssaoRadiusVal.textContent = val.toFixed(0);
        this.sceneManager.setSSAORadius(val);
      });
      ssaoRadiusVal.textContent = parseFloat(ssaoRadiusSlider.value).toFixed(0);
    }
    const ssaoMinDistanceSlider = document.getElementById('debug-ssao-min-distance') as HTMLInputElement;
    const ssaoMinDistanceVal = document.getElementById('debug-ssao-min-distance-val');
    if (ssaoMinDistanceSlider && ssaoMinDistanceVal) {
      ssaoMinDistanceSlider.addEventListener('input', () => {
        const val = parseFloat(ssaoMinDistanceSlider.value);
        ssaoMinDistanceVal.textContent = val.toFixed(3);
        this.sceneManager.setSSAOMinDistance(val);
      });
      ssaoMinDistanceVal.textContent = parseFloat(ssaoMinDistanceSlider.value).toFixed(3);
    }
    const ssaoMaxDistanceSlider = document.getElementById('debug-ssao-max-distance') as HTMLInputElement;
    const ssaoMaxDistanceVal = document.getElementById('debug-ssao-max-distance-val');
    if (ssaoMaxDistanceSlider && ssaoMaxDistanceVal) {
      ssaoMaxDistanceSlider.addEventListener('input', () => {
        const val = parseFloat(ssaoMaxDistanceSlider.value);
        ssaoMaxDistanceVal.textContent = val.toFixed(2);
        this.sceneManager.setSSAOMaxDistance(val);
      });
      ssaoMaxDistanceVal.textContent = parseFloat(ssaoMaxDistanceSlider.value).toFixed(2);
    }
    
    const antialiasCheck = document.getElementById('debug-antialias') as HTMLInputElement;
    if (antialiasCheck) {
      antialiasCheck.addEventListener('change', () => {
        this.sceneManager.setAntialias(antialiasCheck.checked);
      });
    }
    
    const shadowsCheck = document.getElementById('debug-shadows') as HTMLInputElement;
    if (shadowsCheck) {
      shadowsCheck.addEventListener('change', () => {
        this.sceneManager.setShadowMapEnabled(shadowsCheck.checked);
      });
    }
    
    const shadowTypeSelect = document.getElementById('debug-shadow-type') as HTMLSelectElement;
    if (shadowTypeSelect) {
      shadowTypeSelect.addEventListener('change', () => {
        this.sceneManager.setShadowMapType(shadowTypeSelect.value);
      });
    }

    // New shadow controls
    const shadowsEnabledCheck = document.getElementById('debug-shadows-enabled') as HTMLInputElement;
    if (shadowsEnabledCheck) {
      shadowsEnabledCheck.addEventListener('change', () => {
        this.sceneManager.setShadowMapEnabled(shadowsEnabledCheck.checked);
      });
    }

    const shadowQualitySelect = document.getElementById('debug-shadow-quality') as HTMLSelectElement;
    if (shadowQualitySelect) {
      shadowQualitySelect.addEventListener('change', () => {
        const size = parseInt(shadowQualitySelect.value);
        this.sceneManager.setShadowMapQuality(size);
      });
    }

    const shadowBiasSlider = document.getElementById('debug-shadow-bias') as HTMLInputElement;
    const shadowBiasVal = document.getElementById('debug-shadow-bias-val');
    if (shadowBiasSlider && shadowBiasVal) {
      shadowBiasSlider.addEventListener('input', () => {
        const val = parseFloat(shadowBiasSlider.value);
        shadowBiasVal.textContent = val.toFixed(5);
        this.sceneManager.setShadowBias(val);
      });
      shadowBiasVal.textContent = parseFloat(shadowBiasSlider.value).toFixed(5);
    }
    
    const toneMappingSelect = document.getElementById('debug-tone-mapping') as HTMLSelectElement;
    if (toneMappingSelect) {
      toneMappingSelect.addEventListener('change', () => {
        this.sceneManager.setToneMapping(toneMappingSelect.value);
      });
    }
    
    const colorSpaceSelect = document.getElementById('debug-color-space') as HTMLSelectElement;
    if (colorSpaceSelect) {
      colorSpaceSelect.addEventListener('change', () => {
        this.sceneManager.setOutputColorSpace(colorSpaceSelect.value);
      });
    }
    
    // Render Scale для supersampling
    const renderScaleSlider = document.getElementById('debug-render-scale') as HTMLInputElement;
    const renderScaleVal = document.getElementById('debug-render-scale-val');
    if (renderScaleSlider && renderScaleVal) {
      renderScaleSlider.addEventListener('input', () => {
        const val = parseFloat(renderScaleSlider.value);
        renderScaleVal.textContent = val.toFixed(1);
        this.sceneManager.setComposerRenderScale(val);
      });
      renderScaleVal.textContent = renderScaleSlider.value;
    }
    
    const cameraNearSlider = document.getElementById('debug-camera-near') as HTMLInputElement;
    const cameraNearVal = document.getElementById('debug-camera-near-val');
    if (cameraNearSlider && cameraNearVal) {
      cameraNearSlider.addEventListener('input', () => {
        const val = parseFloat(cameraNearSlider.value);
        cameraNearVal.textContent = val.toFixed(2);
        this.sceneManager.setCameraNear(val);
      });
      cameraNearVal.textContent = cameraNearSlider.value;
    }
    
    const cameraFarSlider = document.getElementById('debug-camera-far') as HTMLInputElement;
    const cameraFarVal = document.getElementById('debug-camera-far-val');
    if (cameraFarSlider && cameraFarVal) {
      cameraFarSlider.addEventListener('input', () => {
        const val = parseFloat(cameraFarSlider.value);
        cameraFarVal.textContent = val.toFixed(0);
        this.sceneManager.setCameraFar(val);
      });
      cameraFarVal.textContent = cameraFarSlider.value;
    }
    
    const fogDensitySlider = document.getElementById('debug-fog-density') as HTMLInputElement;
    const fogDensityVal = document.getElementById('debug-fog-density-val');
    if (fogDensitySlider && fogDensityVal) {
      fogDensitySlider.addEventListener('input', () => {
        const val = parseFloat(fogDensitySlider.value);
        fogDensityVal.textContent = val.toFixed(2);
        this.sceneManager.setFogDensity(val);
      });
      fogDensityVal.textContent = fogDensitySlider.value;
    }
    
    const printOffsetFactorSlider = document.getElementById('debug-print-offset-factor') as HTMLInputElement;
    const printOffsetFactorVal = document.getElementById('debug-print-offset-factor-val');
    const printOffsetUnitsSlider = document.getElementById('debug-print-offset-units') as HTMLInputElement;
    const printOffsetUnitsVal = document.getElementById('debug-print-offset-units-val');
    
    const updatePrintOffset = () => {
      const factor = parseFloat(printOffsetFactorSlider.value);
      const units = parseFloat(printOffsetUnitsSlider.value);
      if (printOffsetFactorVal) printOffsetFactorVal.textContent = factor.toFixed(1);
      if (printOffsetUnitsVal) printOffsetUnitsVal.textContent = units.toFixed(1);
      this.sceneManager.setPrintOffsetDebug(factor, units);
    };
    
    if (printOffsetFactorSlider) {
      printOffsetFactorSlider.addEventListener('input', updatePrintOffset);
      if (printOffsetFactorVal) printOffsetFactorVal.textContent = printOffsetFactorSlider.value;
    }
    
    if (printOffsetUnitsSlider) {
      printOffsetUnitsSlider.addEventListener('input', updatePrintOffset);
      if (printOffsetUnitsVal) printOffsetUnitsVal.textContent = printOffsetUnitsSlider.value;
    }
    
    // Кнопка сброса
    const resetBtn = document.getElementById('debug-reset-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.sceneManager.resetDebugSettings();
        // Обновляем UI элементы к значениям по умолчанию
        if (pixelRatioSlider) pixelRatioSlider.value = '2';
        if (pixelRatioVal) pixelRatioVal.textContent = '2.0';
        if (toneExposureSlider) toneExposureSlider.value = '1.2';
        if (toneExposureVal) toneExposureVal.textContent = '1.2';
        if (bloomEnabledCheck) bloomEnabledCheck.checked = false;
        if (bloomStrengthSlider) bloomStrengthSlider.value = '0.35';
        if (bloomStrengthVal) bloomStrengthVal.textContent = '0.35';
        if (bloomRadiusSlider) bloomRadiusSlider.value = '0.35';
        if (bloomRadiusVal) bloomRadiusVal.textContent = '0.35';
        if (bloomThresholdSlider) bloomThresholdSlider.value = '0.85';
        if (bloomThresholdVal) bloomThresholdVal.textContent = '0.85';
        if (dofEnabledCheck) dofEnabledCheck.checked = false;
        if (dofFocusSlider) dofFocusSlider.value = '1.5';
        if (dofFocusVal) dofFocusVal.textContent = '1.50';
        if (dofApertureSlider) dofApertureSlider.value = '0.002';
        if (dofApertureVal) dofApertureVal.textContent = '0.0020';
        if (dofMaxBlurSlider) dofMaxBlurSlider.value = '0.008';
        if (dofMaxBlurVal) dofMaxBlurVal.textContent = '0.0080';
        if (filmEnabledCheck) filmEnabledCheck.checked = false;
        if (filmNoiseSlider) filmNoiseSlider.value = '0.25';
        if (filmNoiseVal) filmNoiseVal.textContent = '0.25';
        if (filmScanlinesSlider) filmScanlinesSlider.value = '0.15';
        if (filmScanlinesVal) filmScanlinesVal.textContent = '0.15';
        if (filmScanlinesCountSlider) filmScanlinesCountSlider.value = '800';
        if (filmScanlinesCountVal) filmScanlinesCountVal.textContent = '800';
        if (ccEnabledCheck) ccEnabledCheck.checked = false;
        if (ccBrightnessSlider) ccBrightnessSlider.value = '0';
        if (ccBrightnessVal) ccBrightnessVal.textContent = '0.00';
        if (ccContrastSlider) ccContrastSlider.value = '0';
        if (ccContrastVal) ccContrastVal.textContent = '0.00';
        if (ccSaturationSlider) ccSaturationSlider.value = '0';
        if (ccSaturationVal) ccSaturationVal.textContent = '0.00';
        if (antialiasCheck) antialiasCheck.checked = true;
        if (shadowsCheck) shadowsCheck.checked = true;
        if (shadowTypeSelect) shadowTypeSelect.value = 'PCFSoftShadowMap';
        if (toneMappingSelect) toneMappingSelect.value = 'ACESFilmicToneMapping';
        if (colorSpaceSelect) colorSpaceSelect.value = 'SRGBColorSpace';
        if (renderScaleSlider) renderScaleSlider.value = '1.5';
        if (renderScaleVal) renderScaleVal.textContent = '1.5';
        if (cameraNearSlider) cameraNearSlider.value = '0.1';
        if (cameraNearVal) cameraNearVal.textContent = '0.1';
        if (cameraFarSlider) cameraFarSlider.value = '2000';
        if (cameraFarVal) cameraFarVal.textContent = '2000';
        if (fogDensitySlider) fogDensitySlider.value = '0.08';
        if (fogDensityVal) fogDensityVal.textContent = '0.08';
        if (printOffsetFactorSlider) printOffsetFactorSlider.value = '-0.2';
        if (printOffsetFactorVal) printOffsetFactorVal.textContent = '-0.2';
        if (printOffsetUnitsSlider) printOffsetUnitsSlider.value = '0';
        if (printOffsetUnitsVal) printOffsetUnitsVal.textContent = '0';
      });
    }
  }
  
  private updateRotationButtonState(isRotating: boolean): void {
    const btn = document.getElementById('toggle-model-rotation-btn');
    if (btn) {
      if (isRotating) {
        btn.classList.add('active');
        const path = btn.querySelector('svg path');
        if (path) {
          (path as SVGPathElement).setAttribute('fill', '#459CFF');
        }
      } else {
        btn.classList.remove('active');
        const path = btn.querySelector('svg path');
        if (path) {
          (path as SVGPathElement).setAttribute('fill', '#555');
        }
      }
    }
  }
  
  /**
   * Генерирует имя для нового проекта: "Новый проект-1", "Новый проект-2" и т.д.
   */
  private generateNewProjectName(): string {
    const storageKey = 'lastProjectNumber';
    const lastNumber = parseInt(localStorage.getItem(storageKey) || '0', 10);
    const nextNumber = lastNumber + 1;
    localStorage.setItem(storageKey, String(nextNumber));
    return `Новый проект-${nextNumber}`;
  }
  
  /**
   * Обновляет название вкладки браузера
   */
  private updateDocumentTitle(): void {
    const projectName = this.projectFileName 
      ? this.projectFileName.replace(/\.fabp$/i, '') 
      : 'Новый проект';
    updateSeo({
      language: i18n.language,
      projectName,
      isDemoMode: DEMO_MODE
    });
  }
  
  private updateInfoModal(): void {
    // FPS
    const infoFps = document.getElementById('info-fps');
    if (infoFps) infoFps.textContent = String(this.fps);
    
    // Resolution
    const infoResolution = document.getElementById('info-resolution');
    const renderer = this.sceneManager.getRenderer();
    if (infoResolution && renderer) {
      const width = renderer.domElement.width;
      const height = renderer.domElement.height;
      infoResolution.textContent = `${width}×${height}`;
    }
    
    // Objects count
    const infoObjects = document.getElementById('info-objects');
    const scene = this.sceneManager.getScene();
    if (infoObjects && scene) {
      let count = 0;
      scene.traverse((obj: any) => {
        if (obj.isMesh) count++;
      });
      infoObjects.textContent = String(count);
    }
    
    // Polygons count
    const infoPolygons = document.getElementById('info-polygons');
    if (infoPolygons && scene) {
      let triangles = 0;
      scene.traverse((obj: any) => {
        if (obj.isMesh && obj.geometry) {
          const geo = obj.geometry;
          if (geo.index) {
            triangles += geo.index.count / 3;
          } else if (geo.attributes.position) {
            triangles += geo.attributes.position.count / 3;
          }
        }
      });
      infoPolygons.textContent = triangles.toLocaleString('ru-RU');
    }
    
    // Project file name (имя файла .fabp)
    const infoProject = document.getElementById('info-project');
    if (infoProject) {
      if (this.projectFileName) {
        infoProject.textContent = this.projectFileName;
      } else if (this.isNewProject) {
        infoProject.textContent = 'Новый проект';
      } else {
        infoProject.textContent = this.projectName || 'Без названия';
      }
    }
    
    // Work time (текущая сессия + накопленное)
    const infoWorktime = document.getElementById('info-worktime');
    if (infoWorktime) {
      const currentSession = Date.now() - this.projectStartTime;
      const total = this.totalWorkTime + currentSession;
      const hours = Math.floor(total / 3600000);
      const minutes = Math.floor((total % 3600000) / 60000);
      const seconds = Math.floor((total % 60000) / 1000);
      infoWorktime.textContent = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    
    // Renderer info
    const infoRenderer = document.getElementById('info-renderer');
    if (infoRenderer && renderer) {
      const gl = renderer.getContext();
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const gpuRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        // Сокращаем длинные названия
        const shortName = gpuRenderer.length > 30 ? gpuRenderer.substring(0, 27) + '...' : gpuRenderer;
        infoRenderer.textContent = shortName;
      } else {
        infoRenderer.textContent = 'WebGL';
      }
    }
  }

  private setupEventListeners(): void {
    // Cmd+S / Ctrl+S для быстрого сохранения
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        this.quickSave();
      }
    });
    
    // Закрытие color picker Coloris при клике на область 3D сцены
    const canvas3D = this.sceneManager.getRenderer().domElement;
    if (canvas3D) {
      canvas3D.addEventListener('click', (e) => {
        // Закрываем панель Coloris через API
        if (window.Coloris && typeof window.Coloris.close === 'function') {
          window.Coloris.close();
        }
      });
    }
    
    // Предупреждение перед закрытием вкладки о несохраненных изменениях
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
      // Показываем предупреждение только если есть загруженная модель
      const hasModel = this.sceneManager.getCurrentModel() !== null;
      
      if (hasModel) {
        // Стандартный текст браузера
        e.preventDefault();
        // Современные браузеры игнорируют custom текст и показывают свой
        e.returnValue = '';
        return '';
      }
    });
    
    // Model scale change - removed
    
    // Environment change
    window.addEventListener('environmentChange', ((e: CustomEvent) => {
      this.sceneManager.setHDRI(e.detail);
    }) as EventListener);
    
    // Loading toast events
    window.addEventListener('showLoadingToast', ((e: CustomEvent) => {
      const { id, text, progress } = e.detail;
      this.uiManager.showLoadingToast(text, progress, id);
    }) as EventListener);
    
    window.addEventListener('updateLoadingToast', ((e: CustomEvent) => {
      const { id, text, progress } = e.detail;
      this.uiManager.updateLoadingToast(id, text, progress);
    }) as EventListener);
    
    window.addEventListener('hideLoadingToast', ((e: CustomEvent) => {
      const { id } = e.detail;
      this.uiManager.hideLoadingToast(id);
    }) as EventListener);

    // Background change
    window.addEventListener('backgroundChange', ((e: CustomEvent) => {
      this.sceneManager.setBackground(e.detail);
    }) as EventListener);
    
    window.addEventListener('backgroundGradient', ((e: CustomEvent) => {
      const { bottom, top } = e.detail;
      this.sceneManager.setBackgroundGradient(top, bottom);
    }) as EventListener);
    
    // Camera FOV change
    window.addEventListener('cameraFovChange', ((e: CustomEvent) => {
      const value = Number(e.detail);
      this.sceneManager.setCameraFov(value);
    }) as EventListener);
    
    // Reset camera
    window.addEventListener('resetCamera', (() => {
      this.sceneManager.resetCameraToModel();
    }) as EventListener);

    // Rotation control mode: rotate scene (camera) vs rotate model (object)
    window.addEventListener('setRotationControlMode', ((e: CustomEvent) => {
      const mode = (e.detail?.mode === 'model' ? 'model' : 'scene') as 'scene' | 'model';
      this.sceneManager.setRotationControlMode(mode);
    }) as EventListener);
    
    // Language change
    window.addEventListener('setLanguage', ((e: CustomEvent) => {
      i18n.setLanguage(e.detail);
      this.updateDocumentTitle();
    }) as EventListener);
    
    // Toggle model rotation
    window.addEventListener('toggleModelRotation', (() => {
      const isRotating = this.sceneManager.toggleModelRotation();
      this.updateRotationButtonState(isRotating);
    }) as EventListener);
    
    // Stop model rotation (for cinema mode, camera poses, and editor)
    window.addEventListener('stopModelRotation', (() => {
      if (this.sceneManager.isModelRotationEnabled()) {
        this.sceneManager.stopModelRotationImmediate();
        this.updateRotationButtonState(false);
      }
    }) as EventListener);

    // Stop model rotation without resetting angle (for manual drag control)
    window.addEventListener('stopModelRotationPreserve', (() => {
      if (this.sceneManager.isModelRotationEnabled()) {
        this.sceneManager.stopModelRotationPreserve();
        this.updateRotationButtonState(false);
      }
    }) as EventListener);

    // Manual rotate model by delta (radians) around Y
    window.addEventListener('rotateModelBy', ((e: CustomEvent) => {
      const deltaY = Number(e.detail?.deltaY || 0);
      if (deltaY) {
        this.sceneManager.rotateModelBy(deltaY);
      }
    }) as EventListener);

    // Stop camera animation (when exiting cinema mode)
    window.addEventListener('stopCameraAnimation', (() => {
      this.sceneManager.stopCameraAnimation();
    }) as EventListener);

    // Photo mode - rotate MODEL to angle preset (not camera)
    window.addEventListener('setModelAngle', ((e: CustomEvent) => {
      const angle = e.detail.angle;
      this.sceneManager.setModelAnglePreset(angle);
    }) as EventListener);
    
    // Reset model angle (when exiting photo mode)
    window.addEventListener('resetModelAngle', (() => {
      this.sceneManager.resetModelAngle();
    }) as EventListener);
    
    // FOV change from photo/cinema mode
    window.addEventListener('setFOV', ((e: CustomEvent) => {
      const fov = e.detail.fov;
      this.sceneManager.setCameraFov(fov);
    }) as EventListener);

    // Camera animation (new events from cinema mode toolbar)
    window.addEventListener('setCameraStart', (() => {
      this.sceneManager.setCameraPoseStart();
    }) as EventListener);
    window.addEventListener('setCameraEnd', (() => {
      this.sceneManager.setCameraPoseEnd();
    }) as EventListener);
    window.addEventListener('playCameraAnimation', (() => {
      this.sceneManager.playCameraAnimation(false);
    }) as EventListener);
    window.addEventListener('setArcMode', ((e: CustomEvent) => {
      const mode = e.detail;
      if (mode === 'turntable') {
        this.sceneManager.setTurntableMode(true);
      } else {
        this.sceneManager.setTurntableMode(false);
        this.sceneManager.setCameraArcMode(mode === 'long');
      }
    }) as EventListener);
    window.addEventListener('setAnimDuration', ((e: CustomEvent) => {
      this.sceneManager.setAnimDuration(e.detail);
    }) as EventListener);
    window.addEventListener('gotoStart', (() => {
      this.sceneManager.gotoStart();
    }) as EventListener);
    window.addEventListener('gotoEnd', (() => {
      this.sceneManager.gotoEnd();
    }) as EventListener);
    window.addEventListener('exportVideo', ((e: CustomEvent) => {
      const aspectRatio = e.detail?.aspectRatio || 'free';
      if (!this.canUseDemoExport('video')) return;
      
      // Проверяем, можно ли экспортировать видео
      if (!this.sceneManager.canExportVideo()) {
        // Показываем сообщение об ошибке в toolbar
        this.sceneManager.showNoFramesError();
        // Не показываем overlay, если нет кадров
        return;
      }

      // Если сейчас идёт превью анимации — останавливаем его,
      // иначе playCameraAnimation() просто сделает return и экспорт "зависнет" на 0%.
      if (this.sceneManager.isAnimationPlaying()) {
        this.sceneManager.stopCameraAnimation();
      }
      
      // Показываем overlay перед экспортом видео
      this.uiManager.showLoadingOverlay(t('loading.export.video'), 0);
      // Вешаем отмену на текущий экспорт
      this.overlayCancelAction = () => {
        this.sceneManager.cancelVideoRender();
        this.uiManager.hideLoadingOverlay();
        const btn = document.getElementById('loading-overlay-cancel');
        btn?.classList.add('hidden');
        this.sceneManager.showToolbarMessage(t('message.render.cancelled'));
      };

      // Показываем кнопку отмены сразу (на случай ранних return/ошибок в SceneManager)
      window.dispatchEvent(new CustomEvent('showCancelButton'));
      this.sceneManager.playCameraAnimation(true, aspectRatio);
    }) as EventListener);
    
    // Показать кнопку отмены в loading overlay
    window.addEventListener('showCancelButton', (() => {
      const cancelBtn = document.getElementById('loading-overlay-cancel');
      if (cancelBtn) {
        cancelBtn.classList.remove('hidden');
      }
    }) as EventListener);
    
    // Обработчик клика по кнопке отмены
    const cancelBtn = document.getElementById('loading-overlay-cancel');
    cancelBtn?.addEventListener('click', () => {
      // Универсальная отмена: текущая операция ставит обработчик сюда
      if (this.overlayCancelAction) {
        const action = this.overlayCancelAction;
        this.overlayCancelAction = null;
        action();
        return;
      }
      // Fallback: старое поведение (видео)
      this.sceneManager.cancelVideoRender();
      this.uiManager.hideLoadingOverlay();
      cancelBtn.classList.add('hidden');
      this.sceneManager.showToolbarMessage(t('message.render.cancelled'));
    });

    // Обработчик основной кнопки в overlay (например: "Сохранить архив")
    const actionBtn = document.getElementById('loading-overlay-action');
    actionBtn?.addEventListener('click', () => {
      if (this.overlayPrimaryAction) {
        const action = this.overlayPrimaryAction;
        this.overlayPrimaryAction = null;
        action();
      }
    });
    
    // Скрыть кнопку отмены при скрытии overlay
    window.addEventListener('hideLoadingOverlay', (() => {
      const cancelBtn = document.getElementById('loading-overlay-cancel');
      if (cancelBtn) {
        cancelBtn.classList.add('hidden');
      }
      const actionBtn = document.getElementById('loading-overlay-action');
      if (actionBtn) {
        actionBtn.classList.add('hidden');
      }
      this.overlayCancelAction = null;
      this.overlayPrimaryAction = null;
    }) as EventListener);
    
    // Обработчик для showToolbarMessage из других модулей
    window.addEventListener('showToolbarMessage', ((e: CustomEvent) => {
      const { text, duration } = e.detail;
      this.sceneManager.showToolbarMessage(text, duration || 3000);
    }) as EventListener);

    // Legacy events (keep for compatibility)
    window.addEventListener('cameraPoseStart', (() => {
      this.sceneManager.setCameraPoseStart();
    }) as EventListener);
    window.addEventListener('cameraPoseEnd', (() => {
      this.sceneManager.setCameraPoseEnd();
    }) as EventListener);
    // cameraAnimPreview удалено - теперь используется playCameraAnimation напрямую
    window.addEventListener('cameraAnimSave', (() => {
      if (!this.canUseDemoExport('video')) return;
      this.sceneManager.playCameraAnimation(true);
    }) as EventListener);
    window.addEventListener('cameraArcModeChange', ((e: CustomEvent) => {
      this.sceneManager.setCameraArcMode(e.detail === 'long');
    }) as EventListener);

    // Video capture
    window.addEventListener('takeVideo', (() => {
      if (!this.canUseDemoExport('video')) return;
      // Если сейчас идёт превью анимации — останавливаем его
      if (this.sceneManager.isAnimationPlaying()) {
        this.sceneManager.stopCameraAnimation();
      }
      // Показываем overlay перед экспортом видео
      this.uiManager.showLoadingOverlay(t('loading.export.video'), 0);
      this.overlayCancelAction = () => {
        this.sceneManager.cancelVideoRender();
        this.uiManager.hideLoadingOverlay();
        const btn = document.getElementById('loading-overlay-cancel');
        btn?.classList.add('hidden');
        this.sceneManager.showToolbarMessage(t('message.render.cancelled'));
      };
      window.dispatchEvent(new CustomEvent('showCancelButton'));
      this.sceneManager.recordTurntableVideo();
    }) as EventListener);

    // Обновление overlay прогресса
    window.addEventListener('updateLoadingOverlay', ((e: CustomEvent) => {
      const { text, progress, eta, warning } = e.detail;
      this.uiManager.updateLoadingOverlay(text, progress, eta, warning);
    }) as EventListener);

    // Скрытие overlay
    window.addEventListener('hideLoadingOverlay', (() => {
      this.uiManager.hideLoadingOverlay();
    }) as EventListener);

    // Reset material event
    window.addEventListener('resetMaterial', () => {
      this.materialManager.resetMaterial();
    });

    // Model upload
    window.addEventListener('modelUpload', ((e: CustomEvent) => {
      if (DEMO_MODE) {
        this.showDemoRestriction('load-model');
        return;
      }
      if (!authService.isAdmin()) {
        window.dispatchEvent(new CustomEvent('showToolbarMessage', {
          detail: { text: 'Загрузить модель может только администратор', duration: 2500 }
        }));
        return;
      }
      this.uiManager.showLoadingOverlay(t('loading.load.model'), 0);
      this.sceneManager.loadModel(e.detail);
    }) as EventListener);

    // Open graphic editor with UV background; realtime bakes canvas to model
    window.addEventListener('openGraphicEditor', (() => {
      const currentModel = this.sceneManager.getCurrentModel();
      if (!currentModel) { alert('Сначала загрузите модель'); return; }
      
      // Останавливаем поворот модели при открытии редактора
      if (this.sceneManager.isModelRotationEnabled()) {
        this.sceneManager.stopModelRotationImmediate();
        this.updateRotationButtonState(false);
      }
      
      // Закрываем photo-mode и cinema-mode
      const toolbarUnified = document.querySelector('.toolbar-unified');
      toolbarUnified?.classList.remove('photo-active', 'cinema-active');
      
      // Сброс текущего редактируемого принта — создаём новый
      this.sceneManager.setCurrentEditingPrintId(null);
      let createdPrintId: string | null = null;
      
      this.editorUI.openWithUVBackground(
        currentModel,
        async (dataUrl: string, canvasData?: any, uvGroupIds?: string[]) => {
          const pid =
            createdPrintId ||
            await this.sceneManager.addPrintLayerFromDataURL(dataUrl, undefined, uvGroupIds);
          createdPrintId = pid;

          if (createdPrintId && uvGroupIds) {
            await this.sceneManager.setPrintLayerUVGroups(createdPrintId, uvGroupIds);
          }

          // ВАЖНО: Всегда сохраняем canvasData, если он есть
          if (pid) {
            if (canvasData) {
              this.sceneManager.savePrintCanvasData(pid, canvasData);
            } else {
              // Если canvasData не передан, получаем его из редактора
              try {
                const editorCanvasData = this.editorUI.getEditor().saveCanvasData();
                if (editorCanvasData) {
                  this.sceneManager.savePrintCanvasData(pid, editorCanvasData);
                }
              } catch (e) {
                console.warn('Could not save canvas data:', e);
              }
            }
          }
        },
        async (dataUrl: string) => {
          const uvGroupIds = this.editorUI.getActiveUVGroupIds();
          if (!createdPrintId) {
            createdPrintId = await this.sceneManager.createTempPrintLayer(dataUrl, uvGroupIds);
          } else if (createdPrintId) {
            // ВАЖНО: Обновляем только текстуру на модели для preview
            // canvasData НЕ сохраняем здесь - только при явном применении через onApplyCallback
            this.sceneManager.updatePrintLayerTexture(dataUrl, createdPrintId);
          }
        },
        false,
        undefined,
        // cancelCallback - удаляем временный принт при отмене
        () => {
          if (createdPrintId) {
            this.sceneManager.deletePrintLayer(createdPrintId);
            createdPrintId = null;
          }
        },
        this.sceneManager.getUVGroupNames(),
        null,
        async (uvGroupIds: string[]) => {
          if (createdPrintId) {
            await this.sceneManager.setPrintLayerUVGroups(createdPrintId, uvGroupIds);
          }
        }
      );
    }) as EventListener);
    
    // Edit existing print with UV background; realtime bakes canvas to model
    window.addEventListener('editPrint', (() => {
      const currentModel = this.sceneManager.getCurrentModel();
      if (!currentModel) { alert('Сначала загрузите модель'); return; }
      const printId = this.sceneManager.getCurrentEditingPrintId();
      if (!printId) return;
      const canvasDataRaw = this.sceneManager.getPrintCanvasData(printId);
      let canvasData: any = canvasDataRaw;
      if (typeof canvasDataRaw === 'string') {
        try {
          canvasData = JSON.parse(canvasDataRaw);
        } catch {
          canvasData = null;
        }
      }
      
      // DEBUG: проверяем canvasData
      console.log('🖼️ editPrint: printId=', printId);
      console.log('🖼️ editPrint: canvasData=', canvasData);
      console.log('🖼️ editPrint: canvasData.objects=', canvasData?.objects);
      console.log('🖼️ editPrint: objects count=', canvasData?.objects?.length);
      
      // Синхронно сбрасываем коллбэки до открытия нового редактора, чтобы стейл-коллбэк
      // от предыдущей сессии не обновлял чужой принт в момент переключения
      try {
        const prevEditor = this.editorUI.getEditor();
        prevEditor.setOnChangeCallback(undefined);
        prevEditor.setOnRealtimeCallback(undefined);
      } catch {}

      // Сохраняем оригинальную текстуру для возможности восстановления при отмене
      const originalTextureUrl = this.sceneManager.getPrintTextureAsDataURL(printId);
      
      this.editorUI.openWithUVBackground(
        currentModel,
        async (dataUrl: string, newCanvasData?: any, uvGroupIds?: string[]) => {
          if (uvGroupIds) {
            await this.sceneManager.setPrintLayerUVGroups(printId, uvGroupIds);
          }
          this.sceneManager.updatePrintLayerTexture(dataUrl, printId);
          // ВАЖНО: Всегда сохраняем canvasData при применении изменений
          if (newCanvasData) {
            this.sceneManager.savePrintCanvasData(printId, newCanvasData);
          } else {
            // Если canvasData не передан, получаем его из редактора
            try {
              const editor = this.editorUI.getEditor();
              const editorCanvasData = editor.saveCanvasData();
              if (editorCanvasData) {
                this.sceneManager.savePrintCanvasData(printId, editorCanvasData);
              }
            } catch (e) {
              console.warn('Could not save canvas data:', e);
            }
          }
        },
        undefined, // realtime подключим после восстановления объектов холста
        true,
        printId,
        // cancelCallback - восстанавливаем оригинальную текстуру при отмене
        () => {
          if (originalTextureUrl) {
            this.sceneManager.updatePrintLayerTexture(originalTextureUrl, printId);
          }
        },
        this.sceneManager.getUVGroupNames(),
        this.sceneManager.getPrintLayerUVGroupIds(printId),
        async (uvGroupIds: string[]) => {
          await this.sceneManager.setPrintLayerUVGroups(printId, uvGroupIds);
        }
      );
      
      // ВОССТАНОВЛЕНИЕ ХОЛСТА, затем включаем realtime
      // ВАЖНО: ждём 2 кадра, чтобы editor успел инициализироваться
      // (openWithUVBackground теперь показывает modal и инициализирует editor через requestAnimationFrame)
      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          try {
            const editor = this.editorUI.getEditor();
            if (canvasData && canvasData.objects && canvasData.objects.length > 0) {
              // Есть полноценные данные холста — восстанавливаем их 1:1
              await editor.loadCanvasData(canvasData);
            }
            // Сохраняем начальное состояние (если данных не было — холст остаётся пустым)
            this.editorUI.setInitialCanvasState();
            // Теперь безопасно подключаем realtime и финальное применение:
            // держим их раздельно, чтобы не смешивать "живой" preview и финальный
            // апдейт после завершения трансформации в один и тот же callback.
            const applyRealtimePrintUpdate = (dataUrl: string) => {
              this.sceneManager.updatePrintLayerTexture(dataUrl, printId);
            };
            const applyFinalPrintUpdate = (dataUrl: string) => {
              // Обновляем только текстуру на модели для preview.
              // canvasData НЕ сохраняем здесь - только при явном применении через onApplyCallback.
              this.sceneManager.updatePrintLayerTexture(dataUrl, printId);
            };
            editor.setOnChangeCallback(applyFinalPrintUpdate);
            editor.setOnRealtimeCallback(applyRealtimePrintUpdate);
          } catch (e) {
            console.error('Error loading print canvas data:', e);
          }
        });
      });
    }) as EventListener);

    // Save project (FABP v2 format - ZIP архив)
    window.addEventListener('saveProject', (async () => {
      try {
        if (DEMO_MODE) {
          this.showDemoRestriction('save-project');
          return;
        }
        
        const filename = this.projectName !== 'Без названия' 
          ? `${this.projectName}.fabp`
          : `${this.generateNewProjectName()}.fabp`;
        
        // СНАЧАЛА получаем handle (пока пользовательский жест активен)
        let targetHandle: FileSystemFileHandle | null = null;
        
        if ('showSaveFilePicker' in window) {
          try {
            targetHandle = await (window as any).showSaveFilePicker({
              suggestedName: filename,
              types: [{
                description: 'FABP Project Files',
                accept: { 'application/fabp': ['.fabp'] }
              }]
            });
          } catch (error: any) {
            // Пользователь отменил диалог
            if (error.name === 'AbortError') {
              return;
            }
            throw error;
          }
        }
        
        // Показываем блокирующий overlay
        this.uiManager.showLoadingOverlay(t('loading.save.project'), 0);
        
        // Обновляем накопленное время перед сохранением
        const currentSession = Date.now() - this.projectStartTime;
        this.totalWorkTime += currentSession;
        this.projectStartTime = Date.now(); // Сбрасываем начало сессии
        
        // Передаем время работы в экспорт проекта
        this.sceneManager.setWorkTime(this.totalWorkTime);
        
        // Экспортируем в новый формат FABP v2 (ZIP)
        const blob = await this.sceneManager.exportProjectV2({
          projectName: this.projectName !== 'Без названия' ? this.projectName : undefined,
          textureQuality: 0.85,
          includeThumbnail: true,
        });
        
        // Скрываем overlay
        this.uiManager.hideLoadingOverlay();
        
        // Используем полученный handle
        if (targetHandle) {
          // Сохраняем handle для быстрого сохранения (Cmd+S)
          this.lastSavedFileHandle = targetHandle;
            
          // Сохраняем файл
          const writable = await targetHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          // Обновляем имя проекта и файла
          if (targetHandle.name) {
            this.projectFileName = targetHandle.name;
            this.projectName = targetHandle.name.replace(/\.fabp$/i, '');
          }
          
          // Помечаем что проект больше не новый
          this.isNewProject = false;
          
          // Обновляем название вкладки
          this.updateDocumentTitle();
          
          console.log(`✅ Проект сохранен: ${targetHandle.name} (${formatFileSize(blob.size)})`);
        
        // Сохраняем в localStorage
        if (this.projectName !== 'Без названия') {
          const storageKey = `workTime_${this.projectName}`;
          localStorage.setItem(storageKey, String(this.totalWorkTime));
          }
        } else {
          // Fallback для браузеров без File System Access API
          const a = document.createElement('a');
          a.download = filename;
          a.href = URL.createObjectURL(blob);
          a.click();
          setTimeout(() => URL.revokeObjectURL(a.href), 1000);
          
          this.projectFileName = filename;
          this.isNewProject = false;
          
          // Обновляем название вкладки
          this.updateDocumentTitle();
          
          console.log(`✅ Проект скачан: ${filename} (${formatFileSize(blob.size)})`);
          
          // Сохраняем в localStorage
          if (this.projectName !== 'Без названия') {
            const storageKey = `workTime_${this.projectName}`;
            localStorage.setItem(storageKey, String(this.totalWorkTime));
          }
        }
      } catch (error) {
        this.uiManager.hideLoadingOverlay();
        console.error('Ошибка сохранения проекта:', error);
        alert('Ошибка сохранения проекта: ' + (error as Error).message);
      }
    }) as EventListener);

    // Load project (поддержка FABP v1 JSON и v2 ZIP)
    window.addEventListener('loadProject', ((e: Event) => {
      if (DEMO_MODE) {
        this.showDemoRestriction('load-project');
        return;
      }
      const file = (e as CustomEvent).detail as File;
      (async () => {
        try {
        // Показываем блокирующий overlay и курсор загрузки
        document.body.classList.add('loading-material');
        this.uiManager.showLoadingOverlay(t('loading.load.project'), 0);
        
        // Получаем информацию о формате файла
        const info = await FabpFormat.getInfo(file);
        console.log(`📂 Загрузка проекта: ${file.name} (${formatFileSize(info.fileSize)}, формат ${info.formatVersion})`);
        
        if (info.metadata?.name) {
          console.log(`   Название: ${info.metadata.name}`);
        }
        
        // Загружаем проект через новый универсальный метод
        await this.sceneManager.loadProjectFromFile(file);
        
        // Обновляем имя проекта
        if (info.formatVersion === 'v2' && info.metadata?.name) {
          this.projectName = info.metadata.name;
        } else {
          this.projectName = file.name.replace(/\.(fabp|json)$/i, '');
        }
        
        // Устанавливаем имя файла и флаг "не новый проект"
        this.projectFileName = file.name;
        this.isNewProject = false;
        
        // Сбрасываем handle - при загрузке проекта начинаем с чистого листа
        this.lastSavedFileHandle = null;
        
          this.projectStartTime = Date.now();
        
        // Восстанавливаем накопленное время работы
        // Для v2 время хранится в projectData.totalWorkTime
          const storageKey = `workTime_${this.projectName}`;
          const storedTime = localStorage.getItem(storageKey);
          if (storedTime) {
            const parsed = parseInt(storedTime, 10);
            if (!isNaN(parsed) && parsed > this.totalWorkTime) {
              this.totalWorkTime = parsed;
            }
          }
        
          // Запускаем периодическое сохранение времени
          this.startWorkTimeSaving();
        
        // Скрываем overlay и убираем курсор загрузки
        document.body.classList.remove('loading-material');
        this.uiManager.hideLoadingOverlay();
        
        // Обновляем название вкладки
        this.updateDocumentTitle();
        
        console.log(`✅ Проект загружен: ${this.projectName}`);
        
        } catch (error) {
        document.body.classList.remove('loading-material');
        this.uiManager.hideLoadingOverlay();
          console.error('Ошибка загрузки проекта:', error);
          alert('Ошибка загрузки проекта: ' + (error as Error).message);
        }
      })();
    }) as EventListener);
    
    // При загрузке новой модели сбрасываем время
    window.addEventListener('modelLoaded', (() => {
      this.projectStartTime = Date.now();
      this.totalWorkTime = 0;
      this.projectName = 'Без названия';
      this.projectFileName = '';
      this.isNewProject = true; // Новая модель = новый проект
      this.lastSavedFileHandle = null; // Сбрасываем handle для быстрого сохранения
      this.stopWorkTimeSaving();
      this.updateDocumentTitle(); // Обновляем title на "Новый проект – fabrica"
      
      // Reset per-project UI state
      this.fpsPerfPanelDismissed = false;
      this.sceneManager.setFpsPanelDismissed(false);
      if (this.fpsPerfPanelMessage) this.fpsPerfPanelMessage.textContent = t('perf.low.message');
      
      // Скрываем loading overlay после загрузки модели
      this.uiManager.hideLoadingOverlay();
    }) as EventListener);

    // Screenshot
    window.addEventListener('takeScreenshot', ((e: CustomEvent) => {
      const aspectRatio = e.detail?.aspectRatio || 'free';
      if (!this.canUseDemoExport('photo')) return;
      void this.sceneManager.takeScreenshot(aspectRatio);
    }) as EventListener);

    // Photo: batch export screenshots for all photo angles (1..8)
    window.addEventListener('exportPhotoBatch', ((e: CustomEvent) => {
      const aspectRatio = e.detail?.aspectRatio || 'free';
      if (!this.canUseDemoExport('photo')) return;

      (async () => {
        let cancelled = false;
        const cancelBtn = document.getElementById('loading-overlay-cancel');
        const actionBtn = document.getElementById('loading-overlay-action');

        this.uiManager.showLoadingOverlay(t('loading.export.photos'), 0);
        window.dispatchEvent(new CustomEvent('showCancelButton'));
        actionBtn?.classList.add('hidden');

        this.overlayCancelAction = () => {
          cancelled = true;
          this.uiManager.hideLoadingOverlay();
          cancelBtn?.classList.add('hidden');
          actionBtn?.classList.add('hidden');
          this.sceneManager.showToolbarMessage(t('message.render.cancelled'));
        };
        this.overlayPrimaryAction = null;

        const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        const setActiveAngleBtn = (angle: number) => {
          document.querySelectorAll('.photo-angle-btn').forEach(b => {
            b.classList.toggle('active', b.getAttribute('data-angle') === String(angle));
          });
        };

        const zip = new JSZip();

        for (let angle = 1; angle <= 8; angle++) {
          if (cancelled) break;

          setActiveAngleBtn(angle);
          window.dispatchEvent(new CustomEvent('setModelAngle', { detail: { angle } }));

          // Ждём окончания анимации ракурса (в SceneManager ~400ms)
          await sleep(460);
          await new Promise(requestAnimationFrame);

          if (cancelled) break;

          const progress = Math.round(((angle - 1) / 8) * 80);
          window.dispatchEvent(new CustomEvent('updateLoadingOverlay', {
            detail: { text: `${t('loading.export.photos')} (${angle}/8)`, progress }
          }));

          const blob = await this.sceneManager.getScreenshotBlob(aspectRatio);
          zip.file(`photo-${aspectRatio}-view-${angle}.png`, blob);

          await sleep(60);
        }

        if (cancelled) return;

        // Этап упаковки ZIP (80..100%)
        window.dispatchEvent(new CustomEvent('updateLoadingOverlay', {
          detail: { text: t('loading.export.photos.zip'), progress: 80 }
        }));

        const zipBlob = await zip.generateAsync(
          { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } },
          (meta) => {
            if (cancelled) return;
            const pct = 80 + Math.round((meta.percent || 0) * 0.2);
            window.dispatchEvent(new CustomEvent('updateLoadingOverlay', {
              detail: { text: t('loading.export.photos.zip'), progress: Math.min(100, pct) }
            }));
          }
        );

        if (cancelled) return;

        // Архив собран. Важно: showSaveFilePicker требует user-gesture,
        // поэтому предлагаем кнопку "Сохранить архив" прямо в overlay.
        window.dispatchEvent(new CustomEvent('updateLoadingOverlay', {
          detail: { text: t('loading.export.photos.ready'), progress: 100 }
        }));
        actionBtn?.classList.remove('hidden');

        const suggestedName = `photos-${aspectRatio}-${Date.now()}.zip`;
        this.overlayPrimaryAction = async () => {
          try {
            if ('showSaveFilePicker' in window) {
              const handle = await (window as any).showSaveFilePicker({
                suggestedName,
                types: [{
                  description: 'ZIP',
                  accept: { 'application/zip': ['.zip'] }
                }]
              });
              const writable = await handle.createWritable();
              await writable.write(zipBlob);
              await writable.close();
            } else {
              const url = URL.createObjectURL(zipBlob);
              const a = document.createElement('a');
              a.download = suggestedName;
              a.href = url;
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
            window.dispatchEvent(new CustomEvent('hideLoadingOverlay'));
            cancelBtn?.classList.add('hidden');
            actionBtn?.classList.add('hidden');
            this.overlayCancelAction = null;
            this.overlayPrimaryAction = null;
            if (DEMO_MODE) consumeDemoQuota('photo');
            this.sceneManager.showToolbarMessage(t('message.photos.exported'), 2000);
          } catch (err) {
            // Пользователь отменил диалог сохранения — остаёмся на экране "архив готов"
            // и даём попробовать ещё раз или нажать Отмена.
            console.warn('Save zip cancelled/failed:', err);
          }
        };
      })();
    }) as EventListener);
    
    // Toggle UV view - removed

    // Print elevation (offset) change
    window.addEventListener('printOffsetChange', ((e: CustomEvent) => {
      this.sceneManager.setPrintOffset(e.detail);
    }) as EventListener);

  }

  private startAnimation(): void {
    const animate = () => {
      this.animationId = requestAnimationFrame(animate);
      
      // Подсчет FPS
      this.frameCount++;
      const now = performance.now();
      if (now - this.lastFpsUpdate >= 1000) {
        const elapsed = now - this.lastFpsUpdate;
        this.fps = Math.round((this.frameCount * 1000) / elapsed);
        this.frameCount = 0;
        this.lastFpsUpdate = now;
        this.updatePerformanceInfo();
        this.updateFpsPerformancePanel(elapsed);
      }
      
      // Camera movement with WASD disabled
      // this.updateCamera();
      this.sceneManager.animate();
    };
    animate();
  }
  
  private updatePerformanceInfo(): void {
    // Обновляем FPS
    if (this.fpsCounter) {
      this.fpsCounter.textContent = `FPS: ${this.fps}`;
    }
    
    // Информация о canvas редактора
    if (this.canvasInfo) {
      try {
        const editor = this.editorUI.getEditor();
        if (editor) {
          const canvas = editor.getCanvas();
          const width = canvas.getWidth();
          const height = canvas.getHeight();
          this.canvasInfo.textContent = `Canvas: ${width}×${height}`;
        } else {
          this.canvasInfo.textContent = `Canvas: --`;
        }
      } catch {
        this.canvasInfo.textContent = `Canvas: --`;
      }
    }
    
    // Количество объектов на canvas
    if (this.objectsInfo) {
      try {
        const editor = this.editorUI.getEditor();
        if (editor) {
          const canvas = editor.getCanvas();
          const objects = canvas.getObjects();
          this.objectsInfo.textContent = `Objects: ${objects.length}`;
        } else {
          this.objectsInfo.textContent = `Objects: --`;
        }
      } catch {
        this.objectsInfo.textContent = `Objects: --`;
      }
    }
  }

  private updateCamera(): void {
    const keyboardState = this.uiManager.getKeyboardState();
    const camera = this.sceneManager.getCamera();
    const controls = this.sceneManager.getControls();
    
    const forward = (keyboardState.w ? 1 : 0) + (keyboardState.s ? -1 : 0);
    const right = (keyboardState.d ? 1 : 0) + (keyboardState.a ? -1 : 0);
    const up = (keyboardState.q ? 1 : 0) + (keyboardState.e ? -1 : 0);

    if (forward || right || up) {
      this.moveCameraLocal(forward, right, up);
    }
  }

  private moveCameraLocal(forward: number, right: number, up: number): void {
    const camera = this.sceneManager.getCamera();
    const controls = this.sceneManager.getControls();
    
    const distance = camera.position.distanceTo(controls.target);
    const speed = Math.max(0.3, distance) * 0.8 * 0.016; // Assuming 60fps
    
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction).normalize();
    
    const rightVector = new THREE.Vector3().crossVectors(direction, camera.up).normalize();
    const upVector = camera.up.clone().normalize();
    
    const movement = new THREE.Vector3();
    movement.addScaledVector(direction, forward * speed);
    movement.addScaledVector(rightVector, right * speed);
    movement.addScaledVector(upVector, up * speed);
    
    camera.position.add(movement);
    controls.target.add(movement);
  }

  /**
   * Быстрое сохранение проекта (Cmd+S / Ctrl+S)
   * При первом сохранении открывается проводник, далее сохраняет в тот же файл
   */
  private async quickSave(): Promise<void> {
    try {
      if (DEMO_MODE) {
        this.showDemoRestriction('save-project');
        return;
      }
      const filename = this.projectName !== 'Без названия' 
        ? `${this.projectName}.fabp`
        : `${this.generateNewProjectName()}.fabp`;
      
      // Для нового проекта или если нет handle - СНАЧАЛА получаем handle (пока пользовательский жест активен)
      let targetHandle: FileSystemFileHandle | null = null;
      
      if ((this.isNewProject || !this.lastSavedFileHandle) && 'showSaveFilePicker' in window) {
        try {
          targetHandle = await (window as any).showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'FABP Project Files',
              accept: { 'application/fabp': ['.fabp'] }
            }]
          });
        } catch (error: any) {
          // Пользователь отменил диалог
          if (error.name === 'AbortError') {
            return;
          }
          throw error;
        }
      }
      
      // Теперь показываем loader и начинаем экспорт
      if (targetHandle) {
        // Для нового файла показываем полный overlay
        this.uiManager.showLoadingOverlay(t('loading.save.project'));
      } else if (this.lastSavedFileHandle) {
        // Для уже сохраненного проекта показываем только статус в toolbar
        const messageContainer = document.getElementById('toolbar-message');
        const messageText = document.getElementById('toolbar-message-text');
        
        if (messageContainer && messageText) {
          messageText.textContent = 'Сохраняем…';
          messageContainer.classList.remove('opacity-0', 'pointer-events-none');
          messageContainer.classList.add('opacity-100');
        }
      } else {
        // Fallback - показываем overlay
        this.uiManager.showLoadingOverlay(t('loading.save.project'));
      }
      
      // Обновляем накопленное время перед сохранением
      const currentSession = Date.now() - this.projectStartTime;
      this.totalWorkTime += currentSession;
      this.projectStartTime = Date.now(); // Сбрасываем начало сессии
      
      // Передаем время работы в экспорт проекта
      this.sceneManager.setWorkTime(this.totalWorkTime);
      
      // Экспортируем в новый формат FABP v2 (ZIP)
      const blob = await this.sceneManager.exportProjectV2({
        projectName: this.projectName !== 'Без названия' ? this.projectName : undefined,
        textureQuality: 0.85,
        includeThumbnail: true,
      });
      
      // Если есть сохраненный handle - используем его для прямого сохранения
      if (this.lastSavedFileHandle && !this.isNewProject && !targetHandle) {
        try {
          // Проверяем права на запись
          const permission = await this.lastSavedFileHandle.queryPermission({ mode: 'readwrite' });
          
          if (permission === 'granted' || permission === 'prompt') {
            // Создаем writable stream и записываем файл
            const writable = await this.lastSavedFileHandle.createWritable();
            await writable.write(blob);
            await writable.close();
            
            // Показываем успех
            const messageContainer = document.getElementById('toolbar-message');
            const messageText = document.getElementById('toolbar-message-text');
            
            if (messageContainer && messageText) {
              messageText.textContent = 'Сохранено ✓';
              setTimeout(() => {
                messageContainer.classList.remove('opacity-100');
                messageContainer.classList.add('opacity-0', 'pointer-events-none');
              }, 3000);
            }
            
            // Обновляем название вкладки
            this.updateDocumentTitle();
            
            console.log(`✅ Проект сохранен: ${this.lastSavedFileHandle.name} (${formatFileSize(blob.size)})`);
            
            // Сохраняем в localStorage
            if (this.projectName !== 'Без названия') {
              const storageKey = `workTime_${this.projectName}`;
              localStorage.setItem(storageKey, String(this.totalWorkTime));
            }
            
            return;
          }
        } catch (error) {
          console.warn('Не удалось использовать сохраненный handle, открываем диалог:', error);
          this.lastSavedFileHandle = null; // Сбрасываем handle
        }
      }
      
      // Используем полученный ранее handle для нового проекта
      if (targetHandle) {
        // Сохраняем handle для будущих сохранений
        this.lastSavedFileHandle = targetHandle;
        
        // Сохраняем файл
        const writable = await targetHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        // Обновляем имя проекта из имени файла
        if (targetHandle.name) {
          this.projectFileName = targetHandle.name;
          if (targetHandle.name !== filename) {
            this.projectName = targetHandle.name.replace(/\.fabp$/i, '');
          }
        }
        
        // Помечаем что проект больше не новый
        this.isNewProject = false;
        
        // Обновляем название вкладки
        this.updateDocumentTitle();
        
        // Скрываем overlay
        this.uiManager.hideLoadingOverlay();
        
        // Показываем успех в toolbar
        const messageContainer = document.getElementById('toolbar-message');
        const messageText = document.getElementById('toolbar-message-text');
        
        if (messageContainer && messageText) {
          messageText.textContent = 'Сохранено ✓';
          setTimeout(() => {
            messageContainer.classList.remove('opacity-100');
            messageContainer.classList.add('opacity-0', 'pointer-events-none');
          }, 3000);
        }
        
        console.log(`✅ Проект сохранен: ${targetHandle.name} (${formatFileSize(blob.size)})`);
        
        // Сохраняем в localStorage
        if (this.projectName !== 'Без названия') {
          const storageKey = `workTime_${this.projectName}`;
          localStorage.setItem(storageKey, String(this.totalWorkTime));
        }
      } else if (!('showSaveFilePicker' in window)) {
        // Fallback для браузеров без File System Access API
        const a = document.createElement('a');
        a.download = filename;
        a.href = URL.createObjectURL(blob);
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
        
        this.projectFileName = filename;
        this.isNewProject = false;
        
        // Обновляем название вкладки
        this.updateDocumentTitle();
        
        // Скрываем overlay для нового проекта
        this.uiManager.hideLoadingOverlay();
        
        // Показываем успех в toolbar
        const messageContainer = document.getElementById('toolbar-message');
        const messageText = document.getElementById('toolbar-message-text');
        
        if (messageContainer && messageText) {
          messageText.textContent = 'Сохранено ✓';
          setTimeout(() => {
            messageContainer.classList.remove('opacity-100');
            messageContainer.classList.add('opacity-0', 'pointer-events-none');
          }, 3000);
        }
        
        console.log(`✅ Проект скачан: ${filename} (${formatFileSize(blob.size)})`);
        
        // Сохраняем в localStorage
        if (this.projectName !== 'Без названия') {
          const storageKey = `workTime_${this.projectName}`;
          localStorage.setItem(storageKey, String(this.totalWorkTime));
        }
      }
      
      // Финальная страховка - закрываем overlay если он еще открыт
      // (на случай если ни одна из веток выше не выполнилась)
      this.uiManager.hideLoadingOverlay();
    } catch (error) {
      // Скрываем overlay при ошибке
      this.uiManager.hideLoadingOverlay();
      console.error('Ошибка быстрого сохранения:', error);
      
      const messageContainer = document.getElementById('toolbar-message');
      const messageText = document.getElementById('toolbar-message-text');
      
      if (messageContainer && messageText) {
        messageText.textContent = 'Ошибка сохранения';
        messageContainer.classList.remove('opacity-0', 'pointer-events-none');
        messageContainer.classList.add('opacity-100');
        setTimeout(() => {
          messageContainer.classList.remove('opacity-100');
          messageContainer.classList.add('opacity-0', 'pointer-events-none');
        }, 3000);
      }
      
      alert('Ошибка сохранения проекта: ' + (error as Error).message);
    }
  }

  private async loadPresets(): Promise<void> {
    try {
      await this.sceneManager.loadLightPresets();
    } catch (error) {
      console.warn('Could not load presets:', error);
    }
  }

  private startWorkTimeSaving(): void {
    this.stopWorkTimeSaving(); // Останавливаем предыдущий интервал если есть
    // Сохраняем время работы в localStorage каждые 10 секунд
    this.workTimeSaveInterval = window.setInterval(() => {
      if (this.projectName !== 'Без названия') {
        const currentSession = Date.now() - this.projectStartTime;
        const total = this.totalWorkTime + currentSession;
        const storageKey = `workTime_${this.projectName}`;
        localStorage.setItem(storageKey, String(total));
      }
    }, 10000);
  }
  
  private stopWorkTimeSaving(): void {
    if (this.workTimeSaveInterval) {
      clearInterval(this.workTimeSaveInterval);
      this.workTimeSaveInterval = 0;
    }
  }

  public destroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }
}

// Initialize the app when DOM is loaded
document.addEventListener('DOMContentLoaded', async () => {
  // Показываем loading overlay сразу при загрузке (режим первой загрузки: только лого + проценты)
  const loadingOverlay = document.getElementById('loading-overlay');
  const loadingText = document.getElementById('loading-overlay-text');
  const loadingProgress = document.getElementById('loading-overlay-progress');
  
  if (loadingOverlay) {
    loadingOverlay.classList.remove('hidden');
    loadingOverlay.classList.add('loading-overlay-initial');
  }
  
  // Этап 1: Инициализация (0%)
  if (loadingProgress) loadingProgress.textContent = '0%';
  
  // Инициализируем локализацию
  i18n.updateAllElements();
  updateSeo({ language: i18n.language, isDemoMode: DEMO_MODE });
  
  // Этап 2: Локализация (10%)
  if (loadingProgress) loadingProgress.textContent = '10%';
  
  // Этап 3: Подготовка интерфейса (20%)
  if (loadingProgress) loadingProgress.textContent = '20%';
  
  // Скрываем весь контент до авторизации
  hideAppContent();

  if (DEMO_MODE) {
    updateSeo({ language: i18n.language, isDemoMode: true });
    subscriptionActive = true;
    (window as any).isSubscriptionActive = () => true;
    showAppContent();
    currentApp = new App();
    ensureDemoBadge();
    requestAnimationFrame(() => {
      setTimeout(() => {
        const progressEl = document.getElementById('loading-overlay-progress');
        if (progressEl) progressEl.textContent = '100%';
      }, 100);
    });
    return;
  }
  
  // Этап 4: Проверка авторизации (30%)
  if (loadingProgress) loadingProgress.textContent = '30%';
  
  // Прямая проверка localStorage (только в dev)
  if (import.meta.env.DEV) {
    const storedToken = localStorage.getItem('fabrica_auth_token');
    const storedUser = localStorage.getItem('fabrica_user');
    console.log('🔐 Direct localStorage check:', {
      hasToken: !!storedToken,
      hasUser: !!storedUser
    });
  }
  
  // Реинициализируем authService на случай если localStorage был недоступен при первой загрузке модуля
  authService.reinit();
  
  // Проверяем авторизацию
  if (!authService.isAuthenticated()) {
    console.log('🔐 Пользователь не авторизован');
    
    // Скрываем loading overlay перед показом окна входа
    const loadingOverlay = document.getElementById('loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
    
    // Показываем окно логина
    loginModal.show(async (user) => {
      console.log('✅ Вошел:', user.email);
      
      // Показываем loading overlay пока проверяем доступ (режим первой загрузки: лого + проценты)
      if (loadingOverlay) {
        loadingOverlay.classList.remove('hidden');
        loadingOverlay.classList.add('loading-overlay-initial');
        const progressEl = document.getElementById('loading-overlay-progress');
        if (progressEl) progressEl.textContent = '';
      }
      
      // После успешного логина проверяем доступ и запускаем приложение
      await checkAccessAndStart();
    });
    return;
  }
  
  console.log('🔐 Токен найден, валидируем...');
  
  // Этап 5: Валидация токена (40%)
  if (loadingProgress) loadingProgress.textContent = '40%';
  
  // Валидируем токен перед использованием
  const isTokenValid = await authService.validateToken();
  
  if (!isTokenValid) {
    console.log('🔐 Токен невалиден, требуется повторный вход');
    
    // Скрываем loading overlay перед показом окна входа
    const loadingOverlayInvalid = document.getElementById('loading-overlay');
    if (loadingOverlayInvalid) {
      loadingOverlayInvalid.classList.add('hidden');
    }
    
    // Показываем окно логина
    loginModal.show(async (user) => {
      console.log('✅ Вошел:', user.email);
      
      // Показываем loading overlay пока проверяем доступ (режим первой загрузки: лого + проценты)
      if (loadingOverlayInvalid) {
        loadingOverlayInvalid.classList.remove('hidden');
        loadingOverlayInvalid.classList.add('loading-overlay-initial');
        const progressEl = document.getElementById('loading-overlay-progress');
        if (progressEl) progressEl.textContent = '';
      }
      
      // После успешного логина проверяем доступ и запускаем приложение
      await checkAccessAndStart();
    });
    return;
  }
  
  console.log('🔐 Токен валиден, проверяем доступ...');
  
  // Этап 6: Проверка доступа (50%)
  if (loadingProgress) loadingProgress.textContent = '50%';
  
  // Уже авторизован - проверяем доступ
  await checkAccessAndStart();
});

// Скрыть контент приложения до авторизации
function hideAppContent() {
  // Создаем простой оверлей без спиннера, так как у нас уже есть loading-overlay
  const blocker = document.createElement('div');
  blocker.id = 'auth-blocker';
  blocker.innerHTML = `
    <style>
      #auth-blocker {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: #0a0a0a;
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      #auth-blocker.hidden {
        display: none;
      }
    </style>
  `;
  document.body.appendChild(blocker);
}

// Показать контент приложения после авторизации
function showAppContent() {
  const blocker = document.getElementById('auth-blocker');
  if (blocker) {
    blocker.classList.add('hidden');
  }
}

// Глобальное состояние подписки
let subscriptionActive = true;
let currentApp: App | null = null;

// Проверка доступа и запуск приложения
async function checkAccessAndStart() {
  try {
    const access = await authService.checkAccess() as any;
    
    // Если аккаунт заблокирован - не пускаем вообще
    if (!access.allowed && access.reason === 'Аккаунт заблокирован') {
      showAccessDenied(access.reason);
      return;
    }
    
    // Определяем активна ли подписка
    // Подписка активна если allowed: true ИЛИ если есть canAccessEditor: true (истёкшая подписка)
    const canEnterEditor = access.allowed || access.canAccessEditor;
    subscriptionActive = access.allowed; // Для функционала - только если allowed
    
    if (!canEnterEditor) {
      showAccessDenied(access.reason || 'Доступ запрещен');
      return;
    }
    
    // Делаем доступным глобально для других модулей
    (window as any).isSubscriptionActive = () => subscriptionActive;
    
    console.log('✅ Запуск приложения...', { subscriptionActive, canEnterEditor });
    
    // Этап 6: Загрузка модулей (50%)
    const loadingProgress = document.getElementById('loading-overlay-progress');
    if (loadingProgress) loadingProgress.textContent = '50%';
    
    // Показываем контент
    showAppContent();
    
    // Этап 7: Инициализация сцены (60%)
    if (loadingProgress) loadingProgress.textContent = '60%';
    
    // Запускаем приложение
    currentApp = new App();
    
    // Этап 8: Загрузка ресурсов (80%)
    if (loadingProgress) loadingProgress.textContent = '80%';
    
    // Скрытие loading overlay идёт через UIManager.hideLoadingOverlay() (минимально 3.5 с с момента показа)
    // Только обновляем прогресс на 100%
    requestAnimationFrame(() => {
      setTimeout(() => {
        const loadingProgress = document.getElementById('loading-overlay-progress');
        if (loadingProgress) loadingProgress.textContent = '100%';
      }, 100);
    });
    
    // Добавляем информацию о пользователе в UI
    addUserInfo();

    // Role-gate: загрузка модели доступна только админу (даже при активной подписке)
    const modelUploadBtn = document.getElementById('model-upload') as HTMLInputElement | null;
    if (modelUploadBtn && !authService.isAdmin()) {
      modelUploadBtn.setAttribute('disabled', 'true');
      modelUploadBtn.disabled = true;
    }
    
    // Если подписка истекла - показываем баннер и блокируем функционал
    if (!subscriptionActive) {
      showSubscriptionExpiredBanner();
      blockFunctionality();
    }
    
    // Запускаем периодическую проверку подписки (каждые 5 минут)
    startSubscriptionCheck();
    
    // Загружаем баннеры от администратора
    loadAdminBanners();
    
  } catch (error) {
    console.error('Ошибка проверки доступа:', error);
    // При ошибке сети разрешаем работу если есть токен
    if (authService.isAuthenticated()) {
      console.log('⚠️ Ошибка сети, но токен есть - разрешаем доступ');
      subscriptionActive = true;
      (window as any).isSubscriptionActive = () => subscriptionActive;
      showAppContent();
      currentApp = new App();
      addUserInfo();
    } else {
      showAccessDenied('Ошибка подключения к серверу');
    }
  }
}

// Периодическая проверка подписки (каждые 5 минут)
function startSubscriptionCheck() {
  setInterval(async () => {
    try {
      const access = await authService.checkAccess() as any;
      const wasActive = subscriptionActive;
      subscriptionActive = access.allowed;
      (window as any).isSubscriptionActive = () => subscriptionActive;
      
      // Если статус изменился
      if (wasActive !== subscriptionActive) {
        console.log('🔄 Статус подписки изменился:', { wasActive, now: subscriptionActive });
        
        if (!subscriptionActive) {
          // Подписка истекла - показываем баннер
          showSubscriptionExpiredBanner();
          blockFunctionality();
        } else {
          // Подписка активирована - убираем баннер
          hideSubscriptionExpiredBanner();
          unblockFunctionality();
        }
      }
      
      // Если аккаунт заблокирован - выкидываем
      if (!access.allowed && access.reason === 'Аккаунт заблокирован' && !access.canAccessEditor) {
        authService.logout();
        location.reload();
      }
    } catch (error) {
      console.error('Ошибка периодической проверки подписки:', error);
    }
  }, 5 * 60 * 1000); // 5 минут
}

// Показать баннер об истёкшей подписке
function showSubscriptionExpiredBanner() {
  if (document.getElementById('subscription-expired-banner')) return;
  
  const banner = document.createElement('div');
  banner.id = 'subscription-expired-banner';
  banner.innerHTML = `
    <style>
      #subscription-expired-banner {
        position: fixed;
        top: 8px;
        left: 8px;
        background: linear-gradient(90deg, #f59e0b, #ef4444);
        color: #fff;
        padding: 10px 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        z-index: 900;
        font-size: 13px;
        font-weight: 500;
        border-radius: 8px;
      }
      #subscription-expired-banner .banner-btn {
        background: rgba(255,255,255,0.2);
        border: none;
        color: #fff;
        padding: 6px 14px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }
      #subscription-expired-banner .banner-btn:hover {
        background: rgba(255,255,255,0.3);
      }
    </style>
    <span data-i18n="message.subscription.expired">${i18n.t('message.subscription.expired')}</span>
    <button type="button" class="banner-btn" data-i18n="message.subscription.renew" id="subscription-banner-support-btn">${i18n.t('message.subscription.renew')}</button>
  `;
  document.body.appendChild(banner);
  document.getElementById('subscription-banner-support-btn')?.addEventListener('click', () => {
    showSupportChoice();
  });
}

// Скрыть баннер
function hideSubscriptionExpiredBanner() {
  const banner = document.getElementById('subscription-expired-banner');
  if (banner) banner.remove();
}

// ============ ADMIN BANNERS ============

interface AdminBanner {
  id: string;
  message: string;
  link_url: string | null;
  link_text: string | null;
  button_url: string | null;
  button_text: string | null;
  subscription_types: string;
  show_until: string | null;
}

// Хранилище закрытых баннеров
function getDismissedBanners(): string[] {
  try {
    return JSON.parse(localStorage.getItem('dismissed_banners') || '[]');
  } catch {
    return [];
  }
}

function dismissBanner(id: string) {
  const dismissed = getDismissedBanners();
  if (!dismissed.includes(id)) {
    dismissed.push(id);
    localStorage.setItem('dismissed_banners', JSON.stringify(dismissed));
  }
  const el = document.getElementById(`admin-banner-${id}`);
  if (el) el.remove();
}

async function loadAdminBanners() {
  try {
    if (!authService.isAuthenticated()) return;
    const headers = authService.getAuthHeaders();
    const res = await fetch('/api/auth/banners', {
      headers,
      credentials: 'include'
    });
    
    console.log('📢 Banners API response status:', res.status);
    if (!res.ok) return;
    
    const data = await res.json();
    const banners: AdminBanner[] = data.banners || [];
    const dismissed = getDismissedBanners();
    
    console.log('📢 Banners received:', banners.length, 'dismissed:', dismissed);
    
    // Фильтруем закрытые
    const activeBanners = banners.filter(b => !dismissed.includes(b.id));
    
    console.log('📢 Active banners to show:', activeBanners.length);
    
    // Удаляем старые баннеры
    document.querySelectorAll('.admin-banner').forEach(el => el.remove());
    
    // Показываем активные
    let topOffset = 60; // Отступ сверху
    activeBanners.forEach((banner, idx) => {
      showAdminBanner(banner, topOffset + idx * 50);
    });
  } catch (e) {
    console.error('Failed to load banners:', e);
  }
}

// Добавляем глобальные стили для баннеров один раз
function ensureBannerStyles() {
  if (document.getElementById('admin-banner-styles')) return;
  
  const style = document.createElement('style');
  style.id = 'admin-banner-styles';
  style.textContent = `
    .admin-banner {
      position: fixed;
      left: 8px;
      background: linear-gradient(90deg, #3b82f6, #8b5cf6);
      color: #fff;
      padding: 10px 16px;
      display: flex;
      align-items: center;
      gap: 12px;
      z-index: 850;
      font-size: 13px;
      font-weight: 500;
      border-radius: 8px;
      max-width: 500px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    }
    .admin-banner-text {
      flex: 1;
    }
    .admin-banner-link {
      color: #fff;
      text-decoration: underline;
      opacity: 0.9;
    }
    .admin-banner-link:hover {
      opacity: 1;
    }
    .admin-banner-btn {
      background: rgba(255,255,255,0.2);
      border: none;
      color: #fff;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 500;
      text-decoration: none;
      cursor: pointer;
      transition: background 0.2s;
    }
    .admin-banner-btn:hover {
      background: rgba(255,255,255,0.3);
    }
    .admin-banner-close {
      background: none;
      border: none;
      color: rgba(255,255,255,0.7);
      font-size: 20px;
      cursor: pointer;
      padding: 0 4px;
      line-height: 1;
    }
    .admin-banner-close:hover {
      color: #fff;
    }
  `;
  document.head.appendChild(style);
}

function showAdminBanner(banner: AdminBanner, topOffset: number) {
  ensureBannerStyles();
  
  const el = document.createElement('div');
  el.id = `admin-banner-${banner.id}`;
  el.className = 'admin-banner';
  el.style.top = `${topOffset}px`; // Уникальная позиция для каждого баннера
  
  let content = `<span class="admin-banner-text">${escapeHtml(banner.message)}</span>`;
  
  if (banner.link_url && banner.link_text) {
    content += `<a href="${escapeHtml(banner.link_url)}" target="_blank" class="admin-banner-link">${escapeHtml(banner.link_text)}</a>`;
  }
  
  if (banner.button_url && banner.button_text) {
    content += `<a href="${escapeHtml(banner.button_url)}" target="_blank" class="admin-banner-btn">${escapeHtml(banner.button_text)}</a>`;
  }
  
  content += `<button class="admin-banner-close" data-id="${banner.id}">×</button>`;
  
  el.innerHTML = content;
  
  document.body.appendChild(el);
  
  // Обработчик закрытия
  el.querySelector('.admin-banner-close')?.addEventListener('click', () => {
    dismissBanner(banner.id);
  });
}

function escapeHtml(str: string | null): string {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Заблокировать функционал при истёкшей подписке
function blockFunctionality() {
  // Блокируем кнопки загрузки модели
  const modelUploadBtn = document.getElementById('model-upload');
  const projectUploadBtn = document.getElementById('project-upload');
  
  if (modelUploadBtn) {
    modelUploadBtn.setAttribute('disabled', 'true');
    (modelUploadBtn as HTMLInputElement).disabled = true;
  }
  
  if (projectUploadBtn) {
    projectUploadBtn.setAttribute('disabled', 'true');
    (projectUploadBtn as HTMLInputElement).disabled = true;
  }
  
  // Добавляем обработчик который будет блокировать загрузку
  (window as any).blockModelLoad = true;
}

// Разблокировать функционал
function unblockFunctionality() {
  const modelUploadBtn = document.getElementById('model-upload');
  const projectUploadBtn = document.getElementById('project-upload');
  
  if (modelUploadBtn) {
    // Важно: загрузка модели доступна только админу
    if (authService.isAdmin()) {
      modelUploadBtn.removeAttribute('disabled');
      (modelUploadBtn as HTMLInputElement).disabled = false;
    } else {
      modelUploadBtn.setAttribute('disabled', 'true');
      (modelUploadBtn as HTMLInputElement).disabled = true;
    }
  }
  
  if (projectUploadBtn) {
    projectUploadBtn.removeAttribute('disabled');
    (projectUploadBtn as HTMLInputElement).disabled = false;
  }
  
  (window as any).blockModelLoad = false;
}

// Показать сообщение об отказе в доступе
function showAccessDenied(reason: string) {
  // Скрываем блокер загрузки
  const blocker = document.getElementById('auth-blocker');
  if (blocker) {
    blocker.remove();
  }
  
  // Определяем сообщение и иконку в зависимости от причины
  let icon = '🚫';
  let title = 'Доступ ограничен';
  let message = reason;
  let buttonText = 'Выйти и войти снова';
  
  if (reason === 'Подписка истекла') {
    icon = '⏰';
    title = 'Подписка истекла';
    message = 'Оплатите подписку, чтобы продолжить пользоваться сервисом';
    buttonText = 'Выйти';
  } else if (reason === 'Аккаунт заблокирован') {
    icon = '🔒';
    title = 'Аккаунт заблокирован';
    message = 'Ваш аккаунт был заблокирован. Свяжитесь с поддержкой.';
  }
  
  const overlay = document.createElement('div');
  overlay.innerHTML = `
    <style>
      .access-denied-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: #0a0a0a;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      }
      .access-denied-box {
        background: #1a1a1a;
        border-radius: 16px;
        padding: 48px;
        text-align: center;
        max-width: 420px;
        margin: 20px;
      }
      .access-denied-icon {
        font-size: 64px;
        margin-bottom: 24px;
      }
      .access-denied-title {
        font-size: 28px;
        font-weight: 600;
        color: #fff;
        margin-bottom: 16px;
      }
      .access-denied-reason {
        color: #888;
        margin-bottom: 32px;
        line-height: 1.6;
        font-size: 16px;
      }
      .access-denied-btn {
        background: #4a9eff;
        color: #fff;
        border: none;
        border-radius: 8px;
        padding: 14px 32px;
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      }
      .access-denied-btn:hover {
        background: #3a8eef;
      }
    </style>
    <div class="access-denied-overlay">
      <div class="access-denied-box">
        <div class="access-denied-icon">${icon}</div>
        <div class="access-denied-title">${title}</div>
        <div class="access-denied-reason">${message}</div>
        <button class="access-denied-btn" onclick="window.authService.logout(); location.reload();">
          ${buttonText}
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay.firstElementChild!);
  
  // Делаем authService доступным глобально для кнопки
  (window as any).authService = authService;
}

// Добавить информацию о пользователе в интерфейс
function addUserInfo() {
  const user = authService.getUser();
  if (!user) return;
  
  // Форматируем дату
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  // Определяем цвет подписки
  const subscriptionColors: Record<string, string> = {
    free: '#666',
    basic: '#3b82f6',
    pro: '#4a9eff',
    enterprise: '#f59e0b'
  };
  const subColor = subscriptionColors[user.subscription_type] || '#666';
  
  // Создаем элемент с иконкой пользователя
  const userWidget = document.createElement('div');
  userWidget.id = 'user-widget';
  userWidget.innerHTML = `
    <style>
      #user-widget {
        position: fixed;
        top: 16px;
        right: 250px;
        z-index: 1000;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
      }
      
      #user-widget .user-icon-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #1a1a1a;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
      }
      
      #user-widget .user-icon-btn:hover {
        background: #222;
      }
      
      #user-widget .user-icon-btn svg {
        width: 18px;
        height: 18px;
        fill: #888;
      }
      
      #user-widget .user-icon-btn:hover svg {
        fill: #fff;
      }
      
      #user-widget .user-dropdown {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        background: #1a1a1a;
        border-radius: 12px;
        min-width: 200px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.5);
        opacity: 0;
        visibility: hidden;
        transform: translateY(-8px);
        transition: all 0.2s ease;
      }
      
      #user-widget .user-dropdown.visible {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }
      
      #user-widget .user-dropdown-header {
        padding: 16px;
        border-bottom: 1px solid #2a2a2a;
      }
      
      #user-widget .user-email {
        color: #fff;
        font-size: 14px;
        font-weight: 500;
        margin-bottom: 8px;
        word-break: break-all;
      }
      
      #user-widget .user-subscription-row {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      
      #user-widget .user-subscription-badge {
        background: ${subColor}22;
        color: ${subColor};
        padding: 4px 10px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
      }
      
      #user-widget .user-expires {
        color: #666;
        font-size: 11px;
      }
      
      #user-widget .user-dropdown-menu {
        padding: 8px;
      }

      #user-dropdown .user-menu-item {
        width: -webkit-fill-available;
      }
      
      #user-widget .user-menu-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border-radius: 8px;
        color: #aaa;
        font-size: 13px;
        text-decoration: none;
        cursor: pointer;
        transition: all 0.15s;
        border: none;
        background: none;
        width: 100%;
        text-align: left;
      }
      
      #user-widget .user-menu-item:hover {
        background: #252525;
        color: #fff;
      }
      
      #user-widget .user-menu-item svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
        flex-shrink: 0;
      }
      
      #user-widget .user-menu-divider {
        height: 1px;
        background: #2a2a2a;
        margin: 4px 8px;
      }
      
      #user-widget .user-menu-item.logout {
        color: #ef4444;
      }
      
      #user-widget .user-menu-item.logout:hover {
        background: #ef444420;
        color: #ef4444;
      }
      
      #user-widget .user-menu-item.admin {
        color: #a855f7;
      }
      
      #user-widget .user-menu-item.admin:hover {
        background: #a855f720;
        color: #a855f7;
      }
    </style>
    
    <button class="user-icon-btn" id="user-icon-btn" title="${i18n.t('user.account')}">
      <svg viewBox="0 0 24 24">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
      </svg>
    </button>
    
    <div class="user-dropdown" id="user-dropdown">
      <div class="user-dropdown-header">
        <div class="user-email">${user.email}</div>
        <div class="user-subscription-row">
          <span class="user-subscription-badge">${user.subscription_type}</span>
          ${user.subscription_expires_at ? `<span class="user-expires">${i18n.t('user.expires')} ${formatDate(user.subscription_expires_at)}</span>` : ''}
        </div>
      </div>
      
      <div class="user-dropdown-menu">
        ${user.is_admin ? `
        <a href="/admin.html" class="user-menu-item admin">
          <svg viewBox="0 0 24 24"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
          <span data-i18n="user.admin">${i18n.t('user.admin')}</span>
        </a>
        ` : ''}
        
        <a href="#" class="user-menu-item" id="user-menu-tutorial">
          <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/></svg>
          <span data-i18n="user.tutorial">${i18n.t('user.tutorial')}</span>
        </a>
        
        <a href="#" class="user-menu-item" id="user-menu-support">
          <svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z"/></svg>
          <span data-i18n="user.support">${i18n.t('user.support')}</span>
        </a>
        
        <div class="user-menu-divider"></div>
        
        <button class="user-menu-item logout" id="user-menu-logout">
          <svg viewBox="0 0 24 24"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
          <span data-i18n="user.logout">${i18n.t('user.logout')}</span>
        </button>
      </div>
    </div>
  `;
  
  document.body.appendChild(userWidget);
  
  // Обработчики
  const iconBtn = document.getElementById('user-icon-btn');
  const dropdown = document.getElementById('user-dropdown');
  
  // Открыть/закрыть меню
  iconBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown?.classList.toggle('visible');
  });
  
  // Закрыть при клике вне
  document.addEventListener('click', () => {
    dropdown?.classList.remove('visible');
  });
  
  // Не закрывать при клике внутри дропдауна
  dropdown?.addEventListener('click', (e) => {
    e.stopPropagation();
  });
  
  // Выход
  document.getElementById('user-menu-logout')?.addEventListener('click', () => {
    authService.logout();
    location.reload();
  });
  
  // Обучение (пока заглушка)
  document.getElementById('user-menu-tutorial')?.addEventListener('click', (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('showTrainingPanel'));
  });

  // Поддержка — по клику показываем выбор: VK или почта
  document.getElementById('user-menu-support')?.addEventListener('click', (e) => {
    e.preventDefault();
    showSupportChoice();
  });
}

